import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { useForegroundTiming } from '../../hooks/useForegroundTiming';
import { timingId } from '../../utils/foregroundClock';
import type { TimingSurface } from '../../types/learningTiming';
import { useEventTracking } from '../../hooks/useEventTracking';
import { pickLatestExercise, readExerciseHtml } from '../../utils/exerciseVersions';
import * as topicsApi from '../../services/topicsApi';
import { renderToString } from 'katex';
import 'katex/dist/katex.min.css';
import {
  initCourseProgress,
  saveReadingProgress,
  saveVideoProgress,
  saveQuizResult,
  saveSelfExplanation,
  saveSurveyResponse,
  saveExerciseResponse,
  saveExerciseAttempt,
  getExerciseMastery,
  getCourseProgress,
  getTopicCompletedActivities,
  markTopicCompleted as markTopicCompletedApi,
  getUnlockedChapters,
  getCompletedTopicsFromSummary,
  initCourseSummary,
  getCourseLoadData,
  CourseProgress,
  ExerciseResponse,
  MasteryStatus,
  ExerciseAttempt,
  updateCurrentPosition,
  getActivityResponse,
  saveActivityResponse
} from '../../services/studentActivitiesApi';

// ==================== CSS Variables & Theme ====================
// 冷灰蓝（Cool Slate）护眼主题 —— 专注学习，减轻眼睛疲劳。
// 全部配色均已按 WCAG 2.1 验证：正文/链接/按钮文字 >= 4.5:1，非文字 UI 元素 >= 3:1。
const theme = {
  colors: {
    bg: '#F7F9FB',           // 冷调近白背景（柔和不刺眼）
    bgDark: '#EBF0F5',       // 稍深的冷灰蓝（代码块、表头、悬停底色）
    fg: '#2E3440',           // 冷调深石板灰文字（对比度 11.8:1 @ bg）
    fgMuted: '#5A6672',      // 柔和的冷灰蓝次要文字（5.6:1 @ bg，仍满足 AA）
    accent: '#2C7A9E',       // 沉稳的青蓝色强调色（白色文字 4.79:1，满足 AA）
    accentText: '#1B5E77',   // 青蓝色文字专用（7.2:1 @ 白卡片，6.1:1 @ accentLight）
    accentLight: '#E3EEF4',  // 浅青蓝底色（选中/悬停背景）
    card: '#FFFFFF',         // 卡片保持白色
    border: '#DDE3EA',       // 冷灰蓝边框
    success: '#2D6A4F',
    error: '#9D2A2A',
    // 次要强调色（自我解释等辅助区块），与主强调色区分但同属冷色系。
    // 注意：此值同时用于文字，因此必须保持 >= 4.5:1（当前 5.1:1 @ 白卡片）。
    accentSecondary: '#4A6FA5',
  },
  transitions: {
    base: 'all 0.2s ease',
    slow: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
  }
};

// ==================== Styled Components ====================
const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.bg};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: ${theme.colors.fg};
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto 1fr;
  height: 100vh;
  overflow: hidden;
`;

// Progress bar at top - spans full width
const TopProgressBar = styled.div`
  grid-column: 1 / -1;
  height: 3px;
  background: ${theme.colors.bgDark};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.accent} 0%, ${theme.colors.success} 100%);
  transform-origin: left;
  transform: scaleX(${props => props.$progress / 100});
  transition: transform 0.3s ease;
`;

// Sidebar - uses CSS Grid
const Sidebar = styled.aside<{ $collapsed: boolean }>`
  grid-row: 2;
  width: ${props => props.$collapsed ? '0' : '260px'};
  min-width: ${props => props.$collapsed ? '0' : '260px'};
  max-width: ${props => props.$collapsed ? '0' : '260px'};
  background: white;
  border-right: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  overflow: hidden;

  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 100;
    box-shadow: ${props => props.$collapsed ? 'none' : '2px 0 20px rgba(0, 0, 0, 0.15)'};
  }
`;

const SidebarToggle = styled.button`
  position: fixed;
  left: 8px;
  top: 8px;
  width: 28px;
  height: 28px;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  transition: ${theme.transitions.base};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${theme.colors.accentLight};
    border-color: ${theme.colors.accent};
  }

  svg {
    width: 14px;
    height: 14px;
    color: ${theme.colors.fgMuted};
  }
`;

const SidebarHeader = styled.div`
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border-bottom: 1px solid ${theme.colors.border};
  background: white;
  min-height: 52px;
  display: flex;
  align-items: center;
`;

const SidebarTitle = styled.h2`
  font-family: 'Noto Serif SC', Georgia, serif;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
  color: ${theme.colors.fg};
`;

const SidebarSubtitle = styled.span`
  font-size: 0.8rem;
  color: ${theme.colors.fgMuted};
  margin-left: 0.5rem;
  &::before {
    content: '·';
    margin-right: 0.5rem;
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  overflow-y: auto;
  padding: 0.75rem;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 2px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #ccc;
  }
`;

const ChapterGroup = styled.div`
  margin-bottom: 0.25rem;
`;

const ChapterHeader = styled.button<{ $expanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${theme.colors.fg};
  text-align: left;
  border-radius: 6px;
  transition: ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.bgDark};
  }
`;

const CollapseIcon = styled.span<{ $collapsed: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 0.55rem;
  transition: transform 0.3s ease;
  transform: rotate(${props => props.$collapsed ? '-90deg' : '0deg'});
`;

const TopicList = styled.div<{ $collapsed: boolean }>`
  margin-left: 0.75rem;
  max-height: ${props => props.$collapsed ? '0' : '500px'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const CatalogSectionList = styled.div<{ $collapsed: boolean }>`
  margin-left: 0.5rem;
  max-height: ${props => props.$collapsed ? '0' : '5000px'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const CatalogSectionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.5rem;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${theme.colors.fgMuted};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: left;
  transition: ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.bgDark};
    color: ${theme.colors.fg};
  }
`;

const TopicItem = styled.button<{ $active: boolean; $completed: boolean; $locked?: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.5rem 0.5rem;
  padding-left: ${props => props.$active ? '0.75rem' : '0.5rem'};
  background: ${props => props.$active ? theme.colors.accentLight : 'none'};
  border: none;
  border-radius: 4px;
  cursor: ${props => props.$locked ? 'not-allowed' : 'pointer'};
  font-size: 0.8rem;
  color: ${props => props.$locked ? '#bbb' : (props.$active ? theme.colors.accentText : theme.colors.fgMuted)};
  text-align: left;
  transition: ${theme.transitions.base};
  opacity: ${props => props.$locked ? 0.6 : 1};

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
    height: ${props => props.$active ? '60%' : '0'};
    background: ${theme.colors.accent};
    border-radius: 1px;
    transition: height 0.2s ease;
  }

  &:hover {
    ${props => !props.$locked && `
      background: ${theme.colors.accentLight};
      color: ${theme.colors.accentText};
    `}
  }

  ${props => props.$completed && !props.$locked && `
    padding-right: 0.5rem;
  `}
`;

const SidebarFooter = styled.div`
  padding: 0.75rem;
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.bg};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const UserDetails = styled.div``;

const UserName = styled.p`
  font-size: 0.8rem;
  font-weight: 500;
  margin: 0;
`;

const UserProgress = styled.p`
  font-size: 0.7rem;
  color: ${theme.colors.fgMuted};
  margin: 0;
`;

// Main content area - uses CSS Grid
const MainContent = styled.main`
  grid-row: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${theme.colors.bg};
`;

// Header inside main content
const Header = styled.header`
  background: white;
  border-bottom: 1px solid ${theme.colors.border};
  padding: 0.75rem 1.5rem;
  flex-shrink: 0;
  min-height: 52px;
  display: flex;
  align-items: center;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1000px;
  margin: 0 auto;
`;

const ActivityTitle = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${theme.colors.fg};
  padding: 0.25rem 0.6rem;
  background: ${theme.colors.bgDark};
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const FullscreenButton = styled.button`
  padding: 0.4rem;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: ${theme.colors.fgMuted};
  transition: ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.bgDark};
    color: ${theme.colors.accentText};
  }
`;

// Scrollable content wrapper
const ContentScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ddd;
    border-radius: 3px;
  }
`;

// Content container
const ContentContainer = styled.div`
  max-width: min(1080px, 95%);
  margin: 0 auto;
  padding: 1.5rem 2rem 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ContentFade = styled.div`
  animation: fadeUp 0.4s ease forwards;

  @keyframes fadeUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes bounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`;

const ContentTitle = styled.h1`
  font-family: 'Noto Serif SC', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.4;
  margin-bottom: 1.25rem;
  color: ${theme.colors.fg};

  @media (min-width: 768px) {
    font-size: 1.75rem;
  }
`;

const HtmlContent = styled.div`
  color: ${theme.colors.fg};
  line-height: 1.75;
  font-size: 0.95rem;

  h1, h2, h3, h4 {
    font-family: 'Noto Serif SC', Georgia, serif;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    color: ${theme.colors.fg};
  }

  h2 {
    font-size: 1.35rem;
    font-weight: 600;
  }

  h3 {
    font-size: 1.15rem;
    font-weight: 600;
  }

  p {
    margin-bottom: 0.875rem;
  }

  ul, ol {
    margin-left: 1.25rem;
    margin-bottom: 0.875rem;
  }

  li {
    margin-bottom: 0.375rem;
  }

  code {
    background: ${theme.colors.bgDark};
    padding: 0.15rem 0.35rem;
    border-radius: 4px;
    font-family: 'Consolas', monospace;
    font-size: 0.85em;
  }

  pre {
    background: ${theme.colors.bgDark};
    padding: 0.875rem;
    border-radius: 6px;
    overflow-x: auto;
    margin: 0.875rem 0;
  }

  blockquote {
    border-left: 3px solid ${theme.colors.accent};
    padding-left: 0.875rem;
    margin: 0.875rem 0;
    color: ${theme.colors.fgMuted};
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.875rem 0;
  }

  th, td {
    border: 1px solid ${theme.colors.border};
    padding: 0.6rem;
    text-align: left;
  }

  th {
    background: ${theme.colors.bgDark};
    font-weight: 600;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 6px;
  }

  a {
    color: ${theme.colors.accentText};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Warm-theme content-clash override: beats content-authored CSS and
     inline style="..." attributes for a curated, semantically-reliable
     element set. This direct-DOM path (HtmlContent) only renders content
     WITHOUT <script>/<style>/<button> tags (routing regex at the render
     call site) -- so it structurally CANNOT contain a class-based colored-
     header pattern like SandboxedHTMLContent's confirmed ".modal-header"/
     "<header>" case (those require a <style> tag to define the class, which
     would route the content to the iframe path instead). A blanket CSS h1
     exclusion is therefore a safe, narrow-risk simplification here (unlike
     the iframe path, which needs the full luminance-based JS fix -- see
     contentHeadingColorFixScript). */
  h2, h3, h4, h5, h6 {
    color: ${theme.colors.fg} !important;
  }

  a, a:visited, a:hover, a:focus {
    color: ${theme.colors.accentText} !important;
  }

  blockquote {
    color: ${theme.colors.fgMuted} !important;
    border-left-color: ${theme.colors.accent} !important;
    background: transparent !important;
  }

  table {
    border-color: ${theme.colors.border} !important;
  }

  th {
    background: ${theme.colors.bgDark} !important;
    color: ${theme.colors.fg} !important;
    border-color: ${theme.colors.border} !important;
  }
`;

// Bottom navigation
const BottomNav = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 1.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 1rem 1rem;
  }
`;

const BottomNavContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid ${theme.colors.border};
`;

const NavButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.6rem 0.875rem;
  background: none;
  border: none;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.$disabled ? '#ccc' : theme.colors.fgMuted};
  font-size: 0.85rem;
  font-weight: 500;
  border-radius: 6px;
  transition: ${theme.transitions.base};
  opacity: ${props => props.$disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    color: ${theme.colors.accentText};
    background: ${theme.colors.accentLight};
  }
`;

const PrimaryButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 1.25rem;
  background: ${props => (props.$disabled || props.disabled) ? '#ccc' : theme.colors.accent};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${props => (props.$disabled || props.disabled) ? 'not-allowed' : 'pointer'};
  transition: ${theme.transitions.base};
  opacity: ${props => (props.$disabled || props.disabled) ? 0.7 : 1};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 122, 158, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Self-Explanation Section
const SelfExplanationSection = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
`;

const SectionLabel = styled.h4<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.$color || theme.colors.accentSecondary};
  margin: 0 0 0.5rem 0;
`;

const SectionHint = styled.p`
  font-size: 0.8rem;
  color: ${theme.colors.fgMuted};
  margin: 0 0 0.875rem 0;
`;

const ExplanationTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.875rem;
  border: 1px solid ${theme.colors.border};
  border-radius: 6px;
  font-size: 0.9rem;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.accentSecondary};
  }

  &::placeholder {
    color: #aaa;
  }
`;

const UnderstandingLevel = styled.div`
  margin-top: 0.875rem;
`;

const LevelLabel = styled.p`
  font-size: 0.8rem;
  color: ${theme.colors.fgMuted};
  margin: 0 0 0.4rem 0;
`;

const LevelButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const LevelButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.6rem;
  border: 1px solid ${props => props.$active ? theme.colors.accent : theme.colors.border};
  background: ${props => props.$active ? theme.colors.accent : 'white'};
  color: ${props => props.$active ? 'white' : theme.colors.fgMuted};
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.accent};
    background: ${props => props.$active ? theme.colors.accent : theme.colors.accentLight};
    color: ${props => props.$active ? 'white' : theme.colors.accentText};
  }
`;

const SubmitButton = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  padding: 0.875rem;
  margin-top: 0.875rem;
  background: ${props => props.$disabled ? '#ccc' : theme.colors.accent};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: ${theme.transitions.base};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(44, 122, 158, 0.25);
  }
`;

// Mastery Modal Overlay
const ModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.3s, visibility 0.3s;
`;

const MasteryModal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  animation: modalSlideIn 0.3s ease;

  @keyframes modalSlideIn {
    from {
      transform: translateY(-16px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const MasteryIcon = styled.div<{ $type: 'success' | 'warning' }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  background: ${props => props.$type === 'success'
    ? `linear-gradient(135deg, ${theme.colors.success} 0%, #4ade80 100%)`
    : `linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)`};
  color: white;
`;

const MasteryTitle = styled.h2`
  font-family: 'Noto Serif SC', Georgia, serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.fg};
  margin: 0 0 1rem 0;
`;

const MasteryMessage = styled.p`
  font-size: 1rem;
  color: ${theme.colors.fgMuted};
  line-height: 1.6;
  margin: 0 0 2rem 0;
`;

const MasteryProgress = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const ProgressDot = styled.div<{ $filled: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$filled ? theme.colors.success : '#e0e0e0'};
  transition: background 0.3s;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

const ModalButton = styled.button<{ $primary?: boolean }>`
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transitions.base};
  background: ${props => props.$primary
    ? `linear-gradient(135deg, ${theme.colors.success} 0%, #4ade80 100%)`
    : '#f5f5f5'};
  color: ${props => props.$primary ? 'white' : theme.colors.fg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const MasteryProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: ${theme.colors.bgDark};
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${theme.colors.fgMuted};
`;

const ProgressCount = styled.span<{ $color: string }>`
  font-weight: 600;
  color: ${props => props.$color};
`;

// LatexText component for rendering text with LaTeX
const LatexTextSpan = styled.span`
  .katex {
    font-size: 1em;
  }
  .katex-display {
    margin: 0.5em 0;
    overflow-x: auto;
    overflow-y: hidden;
  }
`;

// Sandboxed iframe for rendering HTML with CSS and JavaScript
const SandboxedHTMLContainer = styled.div<{ $autoHeight?: boolean }>`
  width: 100%;
  border-radius: 8px;
  overflow: visible;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.card};

  iframe {
    width: 100%;
    border: none;
    display: block;
  }
`;

// Curated, minimal (color/background/border-color only) override that beats
// content-authored CSS and inline style="..." attributes for a fixed,
// semantically-reliable element set (a, button, blockquote, table/th).
// Defined once and reused by both iframe injection points below (fragment-wrap
// `fullHtml` branch and `isFullHtmlDocument`/`autoResizeScript` branch) so the
// rule set can't drift between the two.
//
// NOTE: heading (h1-h6) text color is deliberately NOT handled here as a
// blanket CSS rule -- see contentHeadingColorFixScript below for why and how
// it's handled instead (a plain "h1,h2..h6 { color: ... !important }" rule
// was tried first and reverted: it broke real, currently-synced content that
// uses a colored header/banner container (e.g. a <header> or ".modal-header"
// with `background: linear-gradient(...); color: white;`) wrapping a heading
// that inherits that light color -- forcing dark text there made the heading
// illegible against its own dark background. Confirmed via
// docs/questions/done/*.html files synced into real Topic exercise content
// (e.g. "总体均值差的假设检验：配对比较.html"'s `.modal-header h2`).
const contentOverrideStyle = `
<style id="warm-theme-content-override">
  a, a:visited, a:hover, a:focus {
    color: ${theme.colors.accentText} !important;
  }
  button {
    background: ${theme.colors.accent} !important;
    color: #FFFFFF !important;
    border-color: ${theme.colors.accent} !important;
  }
  blockquote {
    color: ${theme.colors.fgMuted} !important;
    border-left-color: ${theme.colors.accent} !important;
    background: transparent !important;
  }
  table {
    border-color: ${theme.colors.border} !important;
  }
  th {
    background: ${theme.colors.bgDark} !important;
    color: ${theme.colors.fg} !important;
    border-color: ${theme.colors.border} !important;
  }
</style>`;

// Fixes heading (h1-h6) text color to the warm theme's dark gray, but ONLY
// when a heading's actual effective (nearest ancestor) background is light --
// this is the only generically-correct way to avoid breaking headings that
// legitimately inherit a light/white color from a deliberately colored
// ancestor (hero banner, ".modal-header", etc.): a pure CSS selector, no
// matter how it's scoped (by heading level, specificity, or !important),
// cannot distinguish "heading has no color rule and needs the theme's dark
// text forced" from "heading has no *own* color rule but correctly inherits
// a light one from its container" -- both look identical to a selector-based
// override. Measuring the rendered background luminance is the only way to
// tell them apart. A background-image (gradient) on any ancestor is treated
// conservatively as "don't force" (likely an intentional colored container).
// Re-runs on a MutationObserver so dynamically-swapped content (e.g. the
// "上一个例子"/"再看一个例子" example switcher) gets fixed too.
const contentHeadingColorFixScript = `
<script id="warm-theme-heading-color-fix">
(function() {
  function parseBg(str) {
    if (!str) return null;
    var start = str.indexOf('(');
    var end = str.indexOf(')');
    if (start === -1 || end === -1) return null;
    var parts = str.substring(start + 1, end).split(',').map(function(s) { return parseFloat(s.trim()); });
    var alpha = parts.length === 4 ? parts[3] : 1;
    return { r: parts[0], g: parts[1], b: parts[2], alpha: alpha };
  }
  function relLuminance(r, g, b) {
    function chan(c) {
      var cs = c / 255;
      return cs <= 0.03928 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
    }
    return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
  }
  function effectiveBackgroundIsLight(el) {
    var node = el;
    while (node && node !== document.documentElement) {
      var cs = window.getComputedStyle(node);
      var bgImage = cs.backgroundImage;
      if (bgImage && bgImage !== 'none') {
        return false;
      }
      var bg = parseBg(cs.backgroundColor);
      if (bg && bg.alpha > 0) {
        return relLuminance(bg.r, bg.g, bg.b) > 0.4;
      }
      node = node.parentElement;
    }
    return true;
  }
  function fixHeadingColors() {
    var headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
    for (var i = 0; i < headings.length; i++) {
      var h = headings[i];
      if (effectiveBackgroundIsLight(h)) {
        h.style.setProperty('color', '${theme.colors.fg}', 'important');
      }
    }
  }
  // Run immediately (script executes at the end of <body>, after all
  // preceding content is already parsed, so this catches the common case
  // synchronously with no flash of unstyled headings), then again on
  // load/DOMContentLoaded/delays to catch any late async rendering (KaTeX,
  // images affecting layout, etc.).
  fixHeadingColors();
  document.addEventListener('DOMContentLoaded', fixHeadingColors);
  window.addEventListener('load', fixHeadingColors);
  setTimeout(fixHeadingColors, 100);
  setTimeout(fixHeadingColors, 500);
  var headingFixObserver = new MutationObserver(function() {
    setTimeout(fixHeadingColors, 50);
  });
  headingFixObserver.observe(document.body, { childList: true, subtree: true });
})();
</script>`;


// Component to render HTML in a sandboxed iframe with KaTeX support
const SandboxedHTMLContent: React.FC<{ html: string; timingKey?: string; onReady?: () => void }> = ({ html, timingKey, onReady }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Prepare the full HTML content with KaTeX auto-render
  const fullHtml = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Content</title>
  <link rel="stylesheet" href="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.css">
  <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/katex.min.js"></script>
  <script src="https://cdn.bootcdn.net/ajax/libs/KaTeX/0.16.9/contrib/auto-render.min.js"></script>
  <style>
    html, body {
      margin: 0;
      padding: 0;
      height: auto;
      min-height: 0;
    }
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      line-height: 1.8;
      color: ${theme.colors.fg};
      background: ${theme.colors.card};
    }
    .content-wrapper {
      padding: 20px;
    }
    h1, h2, h3, h4, h5, h6 {
      font-family: 'Noto Serif SC', Georgia, serif;
      margin-top: 1.5em;
      margin-bottom: 0.5em;
      line-height: 1.4;
    }
    h2 { font-size: 1.5rem; }
    h3 { font-size: 1.25rem; }
    p { margin-bottom: 1em; }
    code {
      background: ${theme.colors.bgDark};
      padding: 0.2em 0.4em;
      border-radius: 4px;
      font-family: 'Consolas', monospace;
      font-size: 0.9em;
    }
    pre {
      background: ${theme.colors.bgDark};
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;
      margin: 1rem 0;
    }
    pre code {
      background: none;
      padding: 0;
    }
    blockquote {
      border-left: 4px solid ${theme.colors.accent};
      padding-left: 1rem;
      margin: 1rem 0;
      color: ${theme.colors.fgMuted};
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
    }
    th, td {
      border: 1px solid ${theme.colors.border};
      padding: 0.75rem;
      text-align: left;
    }
    th {
      background: ${theme.colors.bgDark};
      font-weight: 600;
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }
    a {
      color: ${theme.colors.accentText};
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
    .katex { font-size: 1.1em; }
    .katex-display {
      margin: 1em 0;
      overflow-x: auto;
      overflow-y: hidden;
    }
    /* Button styles for interactive content */
    button {
      background: ${theme.colors.accent};
      color: white;
      border: none;
      padding: 0.5rem 1rem;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.9rem;
      transition: all 0.2s ease;
    }
    button:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }
    /* Example box styles */
    .example-box {
      background: ${theme.colors.bgDark};
      border-radius: 8px;
      padding: 1rem;
      margin: 1rem 0;
    }
  </style>
</head>
<body>
  <div class="content-wrapper">
  ${html}
  </div>
  <script>
    // Render LaTeX equations
    document.addEventListener('DOMContentLoaded', function() {
      renderMathInElement(document.body, {
        delimiters: [
          {left: '$$', right: '$$', display: true},
          {left: '$', right: '$', display: false},
          {left: '\\\\[', right: '\\\\]', display: true},
          {left: '\\\\(', right: '\\\\)', display: false}
        ],
        throwOnError: false
      });
    });

    // Auto-resize script for iframe content
    (function() {
      function sendResize() {
        const wrapper = document.querySelector('.content-wrapper');
        const body = document.body;
        const html = document.documentElement;

        // Calculate the actual content height - use wrapper's scrollHeight which includes content + padding
        let height;
        if (wrapper) {
          // wrapper.scrollHeight includes content + padding (20px top + 20px bottom = 40px)
          height = wrapper.scrollHeight;
        } else {
          // Fallback: use body's scrollHeight if no wrapper
          height = body.scrollHeight;
        }

        window.parent.postMessage({ type: 'resize', height: height }, '*');
      }
      window.addEventListener('load', sendResize);
      window.addEventListener('resize', sendResize);
      // Also send after a delay to catch dynamically loaded content
      setTimeout(sendResize, 100);
      setTimeout(sendResize, 500);
      setTimeout(sendResize, 2000);

      // Watch for DOM changes (e.g. showing results/explanations after submit)
      const observer = new MutationObserver(function() {
        setTimeout(sendResize, 50);
      });
      observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    })();
  </script>
  ${contentOverrideStyle}
  ${contentHeadingColorFixScript}
</body>
</html>`;

  // Check if the html is already a full HTML document
  const isFullHtmlDocument = html.trim().toLowerCase().startsWith('<!doctype html');

  // Auto-resize script to inject into iframes
  const autoResizeScript = `
  <script>
    (function() {
      // Initialize exercise tracking
      window.exerciseResponses = [];
      window.questionParameters = {};

      // Helper function for exercise HTML to report answers
      // Usage: window.reportExerciseAnswer('q1', '5', { a: 2, b: 3 }, true)
      window.reportExerciseAnswer = function(exerciseId, answer, parameters, isCorrect, questionText) {
        console.log('[Exercise] Reporting answer:', exerciseId, answer, parameters, isCorrect);
        const existingIndex = window.exerciseResponses.findIndex(r => r.exerciseId === exerciseId);
        const response = {
          exerciseId: exerciseId,
          questionType: parameters ? 'parameterized_choice' : 'choice',
          questionText: questionText || '',
          selectedParameter: parameters || null,
          finalAnswer: answer,
          isCorrect: isCorrect || false,
          isCompleted: true,
          answeredAt: new Date().toISOString()
        };
        if (existingIndex >= 0) {
          window.exerciseResponses[existingIndex] = response;
        } else {
          window.exerciseResponses.push(response);
        }
        // Also store parameters separately
        if (parameters) {
          window.questionParameters[exerciseId] = parameters;
        }

        // IMPORTANT: Send exerciseAttempt to parent whenever a real answer is submitted
        // (not from auto-capture which has no questionText and isCorrect is always false)
        // This ensures mastery tracking works for ALL exercise types (drawing, choice, etc.)
        // Guard: must have questionText to distinguish from auto-capture
        if ((isCorrect === true || isCorrect === false) && questionText) {
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({
              type: 'exerciseAttempt',
              payload: {
                exerciseId: exerciseId,
                questionType: parameters ? 'parameterized_choice' : 'choice',
                questionText: questionText || '',
                selectedParameter: parameters || null,
                selectedParameterId: null,
                finalAnswer: answer,
                correctAnswer: null,
                isCorrect: isCorrect,
                totalTimeSpent: 0
              }
            }, '*');
          }
        }
      };

      // Helper to set question parameters
      window.setQuestionParameters = function(exerciseId, parameters) {
        window.questionParameters[exerciseId] = parameters;
      };

      function sendResize() {
        const wrapper = document.querySelector('.content-wrapper');
        let height;
        if (wrapper) {
          height = wrapper.scrollHeight;
        } else {
          height = document.body.scrollHeight;
        }
        window.parent.postMessage({ type: 'resize', height: height }, '*');
      }
      window.addEventListener('load', sendResize);
      window.addEventListener('resize', sendResize);
      setTimeout(sendResize, 500);
      setTimeout(sendResize, 2000);
      if (typeof MutationObserver !== 'undefined') {
        new MutationObserver(sendResize).observe(document.body, { childList: true, subtree: true });
      }

      // Auto-capture answers from form elements on change
      function setupAutoCapture() {
        // Capture radio button changes
        document.querySelectorAll('input[type="radio"][name]').forEach(radio => {
          radio.addEventListener('change', function() {
            if (this.checked) {
              const name = this.name || this.id || 'unknown';
              const value = this.value;
              console.log('[Exercise] Auto-captured radio:', name, value);
              window.reportExerciseAnswer(name, value, null, false);
            }
          });
        });

        // Capture checkbox changes
        document.querySelectorAll('input[type="checkbox"][name]').forEach(checkbox => {
          checkbox.addEventListener('change', function() {
            const name = this.name || this.id || 'unknown';
            const checkedBoxes = document.querySelectorAll('input[type="checkbox"][name="' + name + '"]:checked');
            const values = Array.from(checkedBoxes).map(cb => cb.value);
            console.log('[Exercise] Auto-captured checkbox:', name, values);
            window.reportExerciseAnswer(name, values, null, false);
          });
        });

        // Capture text input changes (on blur)
        document.querySelectorAll('input[type="text"][name], textarea[name]').forEach(input => {
          input.addEventListener('blur', function() {
            const name = this.name || this.id || 'unknown';
            const value = this.value;
            if (value) {
              console.log('[Exercise] Auto-captured text:', name, value);
              window.reportExerciseAnswer(name, value, null, false);
            }
          });
        });

        // Capture select changes
        document.querySelectorAll('select[name]').forEach(select => {
          select.addEventListener('change', function() {
            const name = this.name || this.id || 'unknown';
            const value = this.value;
            console.log('[Exercise] Auto-captured select:', name, value);
            window.reportExerciseAnswer(name, value, null, false);
          });
        });
      }

      // Run auto-capture setup after DOM is ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupAutoCapture);
      } else {
        setupAutoCapture();
      }
      setTimeout(setupAutoCapture, 1000);

      // Listen for exercise response requests from parent
      window.addEventListener('message', function(event) {
        if (event.data?.type === 'getExerciseResponses') {
          console.log('[Exercise iframe] Received getExerciseResponses request');
          console.log('[Exercise iframe] Current exerciseResponses:', window.exerciseResponses);
          console.log('[Exercise iframe] Current questionParameters:', window.questionParameters);

          let responses = [];

          // 1. Check for window.exerciseResponses array (our injected one)
          if (Array.isArray(window.exerciseResponses) && window.exerciseResponses.length > 0) {
            responses = window.exerciseResponses;
            console.log('[Exercise iframe] Using exerciseResponses array');
          }
          // 2. Check for window.getExerciseResponses function (custom implementation)
          else if (typeof window.getExerciseResponses === 'function') {
            try {
              responses = window.getExerciseResponses();
              console.log('[Exercise iframe] Using getExerciseResponses() function');
            } catch (e) {
              console.error('[Exercise iframe] Error calling getExerciseResponses():', e);
            }
          }
          // 3. Check for window.studentAnswers object
          else if (window.studentAnswers && typeof window.studentAnswers === 'object' && Object.keys(window.studentAnswers).length > 0) {
            responses = Object.entries(window.studentAnswers).map(([id, answer]) => ({
              exerciseId: id,
              finalAnswer: answer,
              isCompleted: true
            }));
            console.log('[Exercise iframe] Using studentAnswers object');
          }
          // 4. Check for inputs with data-exercise-id
          else {
            const inputs = document.querySelectorAll('input[data-exercise-id], textarea[data-exercise-id]');
            if (inputs.length > 0) {
              responses = Array.from(inputs).map(input => ({
                exerciseId: input.getAttribute('data-exercise-id'),
                finalAnswer: input.value,
                isCompleted: input.value.trim() !== ''
              }));
              console.log('[Exercise iframe] Using data-exercise-id inputs');
            }
          }

          // Include question parameters in each response
          responses = responses.map(r => ({
            ...r,
            selectedParameter: window.questionParameters[r.exerciseId] || r.selectedParameter || null
          }));

          console.log('[Exercise iframe] Sending responses to parent:', responses);
          window.parent.postMessage({ type: 'exerciseResponses', payload: responses }, '*');
        }
      });
    })();
  </script>
  ${contentOverrideStyle}
  ${contentHeadingColorFixScript}
</body>`;

  // If it's already a full document, inject auto-resize script before </body>
  // Otherwise wrap it in the full HTML template
  const finalHtml = isFullHtmlDocument
    ? html.replace(/<\/body>/i, autoResizeScript)
    : fullHtml;

  useEffect(() => {
    // Listen for messages from the iframe for height updates
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'resize' && containerRef.current) {
        const iframe = containerRef.current.querySelector('iframe');
        if (iframe) {
          // Use exact height without minimum constraint
          iframe.style.height = `${event.data.height}px`;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <SandboxedHTMLContainer ref={containerRef} $autoHeight={true}>
      <iframe
        ref={iframeRef}
        data-learning-visit={timingKey}
        onLoad={onReady}
        title="Content Preview"
        srcDoc={finalHtml}
        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
        loading="lazy"
        style={{ height: 'auto', minHeight: '100px' }}
      />
    </SandboxedHTMLContainer>
  );
};

// Video Player Component
const VideoPlayerContainer = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  border-radius: 8px;
  overflow: hidden;
  background: #000;

  iframe, video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const VideoPlayer: React.FC<{ url: string; title?: string; onReady?: () => void }> = ({ url, title, onReady }) => {
  // Parse video URL to determine if it's YouTube, Bilibili, Vimeo, or direct video
  const getVideoEmbedUrl = (videoUrl: string): { embedUrl: string; type: 'youtube' | 'bilibili' | 'vimeo' | 'direct' } | null => {
    if (!videoUrl) return null;

    // YouTube patterns
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (youtubeMatch) {
      return {
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}`,
        type: 'youtube'
      };
    }

    // Bilibili patterns
    const biliBvMatch = videoUrl.match(/bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/);
    if (biliBvMatch) {
      return {
        embedUrl: `https://player.bilibili.com/player.html?bvid=${biliBvMatch[1]}&high_quality=1`,
        type: 'bilibili'
      };
    }

    const biliAvMatch = videoUrl.match(/bilibili\.com\/video\/av(\d+)/);
    if (biliAvMatch) {
      return {
        embedUrl: `https://player.bilibili.com/player.html?aid=${biliAvMatch[1]}&high_quality=1`,
        type: 'bilibili'
      };
    }

    // Bilibili short link (b23.tv)
    const biliShortMatch = videoUrl.match(/b23\.tv\/(BV[a-zA-Z0-9]+)/);
    if (biliShortMatch) {
      return {
        embedUrl: `https://player.bilibili.com/player.html?bvid=${biliShortMatch[1]}&high_quality=1`,
        type: 'bilibili'
      };
    }

    // Vimeo pattern
    const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return {
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
        type: 'vimeo'
      };
    }

    // Direct video URL (mp4, webm, etc.)
    if (/\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(videoUrl)) {
      return {
        embedUrl: videoUrl,
        type: 'direct'
      };
    }

    // Default: use the URL as-is in iframe (might be an embed URL already)
    return {
      embedUrl: videoUrl,
      type: 'direct'
    };
  };

  const videoInfo = getVideoEmbedUrl(url);

  if (!videoInfo) {
    return (
      <VideoPlayerContainer>
        <SectionLabel>🎬 视频学习</SectionLabel>
        <div style={{ padding: '2rem', textAlign: 'center', color: theme.colors.fgMuted }}>
          暂无视频内容
        </div>
      </VideoPlayerContainer>
    );
  }

  return (
    <VideoPlayerContainer>
      <SectionLabel>🎬 视频学习</SectionLabel>
      {title && <SectionHint>{title}</SectionHint>}
      <VideoWrapper>
        {videoInfo.type === 'direct' ? (
          <video controls preload="metadata" onLoadedData={onReady}>
            <source src={videoInfo.embedUrl} />
            您的浏览器不支持视频播放
          </video>
        ) : (
          <iframe
            src={videoInfo.embedUrl}
            onLoad={onReady}
            title={title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
      </VideoWrapper>
    </VideoPlayerContainer>
  );
};

// Survey Display Component
const SurveyContainer = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: white;
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SurveyContent = styled.div`
  color: ${theme.colors.fg};
  line-height: 1.8;

  h1, h2, h3, h4 {
    font-family: 'Noto Serif SC', Georgia, serif;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
  }

  p { margin-bottom: 1em; }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }

  th, td {
    border: 1px solid ${theme.colors.border};
    padding: 0.75rem;
    text-align: left;
  }

  th {
    background: ${theme.colors.bgDark};
    font-weight: 600;
  }
`;

// Exercise container and content
const ExerciseContainer = styled.div`
  padding: 1.5rem;
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 1rem;
`;

const ExerciseContent = styled.div`
  color: ${theme.colors.fg};
  line-height: 1.8;

  h1, h2, h3, h4 {
    font-family: 'Noto Serif SC', Georgia, serif;
    margin-top: 1.5em;
    margin-bottom: 0.5em;
    color: ${theme.colors.fg};
  }

  p { margin-bottom: 1em; }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }

  th, td {
    border: 1px solid ${theme.colors.border};
    padding: 0.75rem;
    text-align: left;
  }

  th {
    background: ${theme.colors.bgDark};
    font-weight: 600;
  }

  .question {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background: rgba(45, 106, 79, 0.05);
    border-radius: 8px;
    border-left: 3px solid ${theme.colors.success};
  }

  .options {
    margin-left: 1rem;
    margin-top: 0.75rem;
  }

  .option {
    margin: 0.5rem 0;
    padding: 0.5rem 0.75rem;
    background: ${theme.colors.bgDark};
    border-radius: 4px;
  }

  code {
    background: rgba(139, 92, 246, 0.1);
    padding: 0.2rem 0.4rem;
    border-radius: 4px;
    font-family: 'Fira Code', monospace;
    font-size: 0.9em;
  }

  pre {
    background: ${theme.colors.bgDark};
    padding: 1rem;
    border-radius: 8px;
    overflow-x: auto;
  }

  /* Warm-theme content-clash override: beats content-authored CSS and
     inline style="..." attributes for a curated, semantically-reliable
     element set. This direct-DOM path (ExerciseContent) only renders content
     WITHOUT <script>/<style>/<button> tags (routing regex at the render
     call site) -- so it structurally CANNOT contain a class-based colored-
     header pattern like SandboxedHTMLContent's confirmed ".modal-header"/
     "<header>" case (those require a <style> tag to define the class, which
     would route the content to the iframe path instead). A blanket CSS h1
     exclusion is therefore a safe, narrow-risk simplification here (unlike
     the iframe path, which needs the full luminance-based JS fix -- see
     contentHeadingColorFixScript). */
  h2, h3, h4, h5, h6 {
    color: ${theme.colors.fg} !important;
  }

  a, a:visited, a:hover, a:focus {
    color: ${theme.colors.accentText} !important;
  }

  blockquote {
    color: ${theme.colors.fgMuted} !important;
    border-left-color: ${theme.colors.accent} !important;
    background: transparent !important;
  }

  table {
    border-color: ${theme.colors.border} !important;
  }

  th {
    background: ${theme.colors.bgDark} !important;
    color: ${theme.colors.fg} !important;
    border-color: ${theme.colors.border} !important;
  }
`;

// Loading & Error
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: ${theme.colors.fgMuted};
`;

const ErrorContainer = styled.div`
  background: #fef2f2;
  color: ${theme.colors.error};
  padding: 1.5rem;
  border-radius: 8px;
  margin: 2rem auto;
  max-width: 600px;
  text-align: center;
`;

// Course Entrance Animation Overlay
const EntranceOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: ${theme.colors.bg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.5s ease, visibility 0.5s ease;
`;

const EntranceContent = styled.div`
  text-align: center;
  animation: fadeInUp 0.6s ease forwards;
`;

const EntranceIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  animation: bounce 1s ease infinite;
`;

const EntranceTitle = styled.h1`
  font-family: 'Noto Serif SC', Georgia, serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.fg};
  margin: 0 0 0.5rem 0;
`;

const EntranceSubtitle = styled.p`
  font-size: 1rem;
  color: ${theme.colors.fgMuted};
  margin: 0 0 2rem 0;
`;

const EntranceProgressBar = styled.div`
  width: 280px;
  height: 6px;
  background: ${theme.colors.bgDark};
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const EntranceProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, ${theme.colors.accent} 0%, ${theme.colors.success} 100%);
  border-radius: 3px;
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const EntranceStatus = styled.p`
  font-size: 0.875rem;
  color: ${theme.colors.fgMuted};
  margin: 0;
`;

// ==================== Types ====================
type ActivityName = 'reading' | 'video' | 'review' | 'survey' | 'selfExplanation' | 'exercise' | 'quiz';

interface Activity {
  _id?: string;
  name: ActivityName;
  title: string;
  value: any;
  link?: string;
  sortingIndex: number;
}

interface TopicRef {
  topicId: any;
  activities: Activity[];
}

// Sorted activity item for navigation
interface SortedActivity {
  activity: Activity;
  activityIndex: number;
}

interface Section {
  _id: string;
  title: string;
  description?: string;
  order: number;
  topics: TopicRef[];
}

interface Chapter {
  _id: string;
  title: string;
  description?: string;
  order: number;
  sections?: Section[];
  topics: TopicRef[];
}

interface Course {
  _id: string;
  courseCode: string;
  title: string;
  description: string;
  chapters: Chapter[];
  totalTopics: number;
}

interface Topic {
  _id: string;
  title: string;
  slug: string;
  description: string;
  learningContent?: Array<{
    _id?: string;
    version: string;
    title: string;
    htmlContent: string;
    isLatest: boolean;
  }>;
  keywords?: Array<{
    term: string;
    definition: string;
    importance: string;
  }>;
  exercises?: Array<{
    _id?: string;
    type: string;
    title: string;
    content: any;
    order?: number;
    version?: string;
    userEmail?: string;
    timeStamp?: string;
    isLatest?: boolean;
  }>;
  category?: { _id: string; name: string; slug: string };
  subcategory?: { _id: string; name: string; slug: string } | string;
  subcategoryCode?: string;
  subcategoryName?: string;
  order?: number;
}

interface FlatTopicItem {
  chapterId: string;
  chapterTitle: string;
  sectionId?: string;
  sectionTitle?: string;
  topicId: string;
  topic: Topic | null;
  activities: Activity[];
  completed: boolean;
  index: number;
}

// ==================== Main Component ====================
interface CourseLearnPageProps {
  navigationMode?: 'course' | 'catalog';
}

const createCatalogActivities = (topicId: string): Activity[] => [
  {
    _id: `${topicId}-reading`,
    name: 'reading',
    title: '阅读内容',
    value: true,
    sortingIndex: 0
  },
  {
    _id: `${topicId}-self-explanation`,
    name: 'selfExplanation',
    title: '自我解释',
    value: true,
    sortingIndex: 1
  },
  {
    _id: `${topicId}-exercise`,
    name: 'exercise',
    title: '习题',
    value: true,
    sortingIndex: 2
  }
];

export const CourseLearnPage: React.FC<CourseLearnPageProps> = ({ navigationMode = 'course' }) => {
  const { courseId, topicId: routeTopicId } = useParams<{ courseId: string; topicId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { trackSelfExplanationSubmit } = useEventTracking();
  const isCatalogMode = navigationMode === 'catalog';

  // Course state
  const [course, setCourse] = useState<Course | null>(null);
  const [flatTopics, setFlatTopics] = useState<FlatTopicItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Entrance animation state
  const [showEntrance, setShowEntrance] = useState(true);
  const [entranceProgress, setEntranceProgress] = useState(0);
  const [entranceStatus, setEntranceStatus] = useState('正在打开课程...');

  // UI state
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [unlockedChapters, setUnlockedChapters] = useState<Set<string>>(new Set());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [overallProgress, setOverallProgress] = useState(0);
  const [completedTopics, setCompletedTopics] = useState<Set<string>>(new Set());

  // Learning state - track current activity index within the sorted activities
  const [currentActivityIndex, setCurrentActivityIndex] = useState(0);
  const [explanation, setExplanation] = useState('');
  const [understandingLevel, setUnderstandingLevel] = useState<'low' | 'medium' | 'high' | null>(null);
  const [readingCompleted, setReadingCompleted] = useState(false);
  const [completedActivityIds, setCompletedActivityIds] = useState<Set<string>>(new Set());
  // Track which activity is "in progress" (the first incomplete activity user was working on)
  const [inProgressActivityIndex, setInProgressActivityIndex] = useState<number | null>(null);

  // Survey state
  const [currentSurvey, setCurrentSurvey] = useState<any>(null);

  // Exercise state - store student responses from iframe
  const [exerciseResponses, setExerciseResponses] = useState<ExerciseResponse[]>([]);

  // Exercise mastery state
  const [masteryStatus, setMasteryStatus] = useState<MasteryStatus>({
    masteryStatus: 'in_progress',
    consecutiveCorrect: 0,
    consecutiveIncorrect: 0,
    totalAttempts: 0
  });
  const [showMasteryCelebration, setShowMasteryCelebration] = useState(false);
  const [showRelearnPrompt, setShowRelearnPrompt] = useState(false);

  // Activity tracking state
  const [courseProgress, setCourseProgress] = useState<CourseProgress | null>(null);

  // Lazy loading state for topic details
  const [loadedTopicDetails, setLoadedTopicDetails] = useState<Map<string, Topic>>(new Map());
  const [loadingTopicIds, setLoadingTopicIds] = useState<Set<string>>(new Set());

  // Review mode state
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [showingSummary, setShowingSummary] = useState(false);
  const [reviewActivityResponses, setReviewActivityResponses] = useState<Map<string, any>>(new Map());

  // Track whether initial load has completed (to skip saving position on first render)
  const initialLoadDoneRef = useRef(false);

  // Fetch course data (initial load - only course structure and progress)
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const token = useAuthStore.getState().token;

        if (isCatalogMode) {
          const [{ categories }, firstTopicsPage] = await Promise.all([
            topicsApi.getAllCategories({ hierarchy: true, isActive: false }),
            topicsApi.getAllTopics({ published: false, page: 1, limit: 1000 })
          ]);
          const remainingPages = await Promise.all(
            Array.from(
              { length: Math.max(0, firstTopicsPage.pagination.pages - 1) },
              (_, pageIndex) => topicsApi.getAllTopics({
                published: false,
                page: pageIndex + 2,
                limit: 1000
              })
            )
          );
          const allTopics = [
            ...firstTopicsPage.topics,
            ...remainingPages.flatMap(page => page.topics)
          ] as Array<topicsApi.Topic & {
            subcategory?: { _id?: string } | string;
            subcategoryCode?: string;
            subcategoryName?: string;
          }>;

          const flat: FlatTopicItem[] = [];
          let index = 0;
          const topicIdsInHierarchy = new Set<string>();
          const catalogChapters: Chapter[] = categories.map((category) => {
            const sections: Section[] = (category.subcategories || []).map((subcategory) => {
              const sectionTopics = allTopics.filter((topic) => {
                const subcategoryId = typeof topic.subcategory === 'string'
                  ? topic.subcategory
                  : topic.subcategory?._id;
                return subcategoryId === subcategory._id ||
                  topic.subcategoryCode?.toUpperCase() === subcategory.code.toUpperCase();
              });

              const topicRefs: TopicRef[] = sectionTopics.map((topic) => {
                topicIdsInHierarchy.add(topic._id);
                const activities = createCatalogActivities(topic._id);
                flat.push({
                  chapterId: category._id,
                  chapterTitle: category.chinese_name || category.name,
                  sectionId: subcategory._id,
                  sectionTitle: subcategory.chinese_name || subcategory.name,
                  topicId: topic._id,
                  topic: topic as unknown as Topic,
                  activities,
                  completed: false,
                  index: index++
                });
                return { topicId: topic, activities };
              });

              return {
                _id: subcategory._id,
                title: subcategory.chinese_name || subcategory.name,
                description: subcategory.description,
                order: subcategory.order,
                topics: topicRefs
              };
            }).filter((section) => section.topics.length > 0);

            const categoryTopicsWithoutSubcategory = allTopics.filter((topic) =>
              topic.category?._id === category._id &&
              !topicIdsInHierarchy.has(topic._id)
            );
            if (categoryTopicsWithoutSubcategory.length > 0) {
              const sectionId = `${category._id}-other-topics`;
              const topicRefs = categoryTopicsWithoutSubcategory.map((topic) => {
                topicIdsInHierarchy.add(topic._id);
                const activities = createCatalogActivities(topic._id);
                flat.push({
                  chapterId: category._id,
                  chapterTitle: category.chinese_name || category.name,
                  sectionId,
                  sectionTitle: '其他主题',
                  topicId: topic._id,
                  topic: topic as unknown as Topic,
                  activities,
                  completed: false,
                  index: index++
                });
                return { topicId: topic, activities };
              });
              sections.push({
                _id: sectionId,
                title: '其他主题',
                order: Number.MAX_SAFE_INTEGER,
                topics: topicRefs
              });
            }

            return {
              _id: category._id,
              title: category.chinese_name || category.name,
              description: category.description,
              order: category.order,
              sections,
              topics: []
            };
          }).filter((chapter) => (chapter.sections?.length || 0) > 0);

          const uncategorizedTopics = allTopics.filter((topic) => !topicIdsInHierarchy.has(topic._id));
          if (uncategorizedTopics.length > 0) {
            const chapterId = 'uncategorized';
            const sectionId = 'uncategorized-topics';
            const topicRefs = uncategorizedTopics.map((topic) => {
              const activities = createCatalogActivities(topic._id);
              flat.push({
                chapterId,
                chapterTitle: '未分类',
                sectionId,
                sectionTitle: '其他主题',
                topicId: topic._id,
                topic: topic as unknown as Topic,
                activities,
                completed: false,
                index: index++
              });
              return { topicId: topic, activities };
            });
            catalogChapters.push({
              _id: chapterId,
              title: '未分类',
              order: Number.MAX_SAFE_INTEGER,
              sections: [{
                _id: sectionId,
                title: '其他主题',
                order: 0,
                topics: topicRefs
              }],
              topics: []
            });
          }

          const selectedRouteTopic = routeTopicId ? decodeURIComponent(routeTopicId) : '';
          const matchedTopicIndex = flat.findIndex((item) =>
            item.topicId === selectedRouteTopic ||
            item.topic?.slug?.toLowerCase() === selectedRouteTopic.toLowerCase()
          );
          if (selectedRouteTopic && matchedTopicIndex < 0) {
            setError('未找到该学习主题');
            return;
          }
          const selectedIndex = Math.max(0, matchedTopicIndex);
          const selectedItem = flat[selectedIndex];
          const catalogCourse: Course = {
            _id: 'all-topics',
            courseCode: 'ALL-TOPICS',
            title: '全部主题',
            description: '按分类浏览全部统计学习主题',
            chapters: catalogChapters,
            totalTopics: flat.length
          };

          setCourse(catalogCourse);
          setFlatTopics(flat);
          setUnlockedChapters(new Set(catalogChapters.map((chapter) => chapter._id)));
          setExpandedChapters(new Set(selectedItem ? [selectedItem.chapterId] : []));
          setExpandedSections(new Set(selectedItem?.sectionId ? [selectedItem.sectionId] : []));
          setCurrentTopicIndex(selectedIndex);
          setCurrentActivityIndex(0);
          setInProgressActivityIndex(0);
          setOverallProgress(0);
          return;
        }

        // Fetch course structure (chapters, sections, topics with basic info and activities)
        const courseRes = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/courses/${courseId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const courseData = courseRes.data.data;
        setCourse(courseData);

        // Expand all chapters by default
        const allChapterIds = new Set<string>(courseData.chapters?.map((c: any) => c._id) || []);
        setExpandedChapters(allChapterIds);

        // Helper function to get topic ID from topicRef
        const getTopicId = (topicRef: any): string => {
          if (!topicRef) return '';
          const tid = topicRef.topicId;
          if (typeof tid === 'object' && tid !== null) {
            return tid._id?.toString() || '';
          }
          return tid?.toString() || '';
        };

        // Helper function to get basic topic info from topicRef (title, slug, etc. - but not learningContent)
        const getTopicBasicInfo = (topicRef: any): Topic | null => {
          if (!topicRef) return null;
          const tid = topicRef.topicId;
          if (typeof tid === 'object' && tid !== null) {
            // The course API populates: title, slug, order, category, content
            // We'll use this as the basic topic info (learningContent loaded on demand)
            return {
              _id: tid._id,
              title: tid.title || '',
              slug: tid.slug || '',
              description: tid.description || '',
              order: tid.order || 0,
              category: tid.category,
              content: tid.content,
              // learningContent will be loaded on demand
              learningContent: undefined,
              keywords: undefined,
              exercises: undefined
            } as unknown as Topic;
          }
          return null;
        };

        // Flatten the course structure (with basic topic info, learningContent loaded on demand)
        const flat: FlatTopicItem[] = [];
        let idx = 0;
        const chapterIds: string[] = [];

        for (const chapter of courseData.chapters || []) {
          chapterIds.push(chapter._id);
          // Expand first chapter by default
          if (idx === 0) {
            setExpandedChapters(new Set([chapter._id]));
          }

          // Handle sections if they exist
          if (chapter.sections && chapter.sections.length > 0) {
            for (const section of chapter.sections) {
              for (const topicRef of section.topics || []) {
                const topicId = getTopicId(topicRef);
                const topicBasicInfo = getTopicBasicInfo(topicRef);
                flat.push({
                  chapterId: chapter._id,
                  chapterTitle: chapter.title,
                  sectionId: section._id,
                  sectionTitle: section.title,
                  topicId: topicId,
                  topic: topicBasicInfo, // Basic info (title, etc.) - learningContent loaded on demand
                  activities: topicRef.activities || [],
                  completed: false,
                  index: idx++
                });
              }
            }
          }
          // Handle topics directly in chapter (no sections)
          else if (chapter.topics && chapter.topics.length > 0) {
            for (const topicRef of chapter.topics) {
              const topicId = getTopicId(topicRef);
              const topicBasicInfo = getTopicBasicInfo(topicRef);
              flat.push({
                chapterId: chapter._id,
                chapterTitle: chapter.title,
                topicId: topicId,
                topic: topicBasicInfo, // Basic info (title, etc.) - learningContent loaded on demand
                activities: topicRef.activities || [],
                completed: false,
                index: idx++
              });
            }
          }
        }

        // Helper function to calculate unlocked chapters based on completed topics
        const calculateUnlockedChapters = (completedTopicIds: Set<string>): Set<string> => {
          const unlocked = new Set<string>();
          // First chapter is always unlocked
          if (chapterIds.length > 0) {
            unlocked.add(chapterIds[0]);
          }

          // Check each chapter to see if all its topics are completed
          for (let i = 0; i < chapterIds.length - 1; i++) {
            const currentChapterId = chapterIds[i];
            const nextChapterId = chapterIds[i + 1];

            // Get all topics for current chapter
            const chapterTopics = flat.filter(t => t.chapterId === currentChapterId);

            // Check if all topics in this chapter are completed
            const allTopicsCompleted = chapterTopics.length > 0 &&
              chapterTopics.every(t => completedTopicIds.has(t.topicId));

            if (allTopicsCompleted) {
              unlocked.add(nextChapterId);
            } else {
              // Once we find an incomplete chapter, stop unlocking further chapters
              break;
            }
          }

          return unlocked;
        };

        setFlatTopics(flat);

        // Initialize course progress tracking
        try {
          if (courseId) {
            // ===== Use optimized single API call for all progress data =====
            let loadData;
            try {
              loadData = await getCourseLoadData(courseId);
              console.log('[CourseLoad] Optimized load - Unlocked chapters:', loadData.unlockedChapterIds.length);
              console.log('[CourseLoad] Optimized load - Completed topics:', loadData.completedTopicIds.length);
              console.log('[CourseLoad] Optimized load - Current topic:', loadData.currentTopicId);
            } catch (loadErr) {
              console.warn('[CourseLoad] Optimized load failed, falling back:', loadErr);
              // Fallback: initialize from existing activities
              await initCourseProgress(courseId);
              await initCourseSummary(courseId);
              loadData = await getCourseLoadData(courseId);
            }

            // Restore completed topics
            const completed = new Set<string>(loadData.completedTopicIds);
            setCompletedTopics(completed);

            // Set unlocked chapters
            let unlockedChapterIds = [...loadData.unlockedChapterIds];
            // Always ensure first chapter is unlocked
            if (chapterIds.length > 0 && !unlockedChapterIds.includes(chapterIds[0])) {
              unlockedChapterIds.push(chapterIds[0]);
            }
            // Ensure chapters containing completed topics are always unlocked
            // This guarantees check marks persist even if server data is incomplete
            completed.forEach(topicId => {
              const topicItem = flat.find(t => t.topicId === topicId);
              if (topicItem && !unlockedChapterIds.includes(topicItem.chapterId)) {
                unlockedChapterIds.push(topicItem.chapterId);
              }
            });
            if (unlockedChapterIds.length > 0) {
              setUnlockedChapters(new Set(unlockedChapterIds));
            } else {
              setUnlockedChapters(calculateUnlockedChapters(completed));
            }

            // Only set completed activities for the CURRENT topic
            const allCompletedActivities = new Set<string>();
            const currentTopicId = loadData.currentTopicId;

            if (currentTopicId && loadData.currentTopicCompletedActivities.length > 0) {
              loadData.currentTopicCompletedActivities.forEach(id =>
                allCompletedActivities.add(`${currentTopicId}:${id}`)
              );
            }
            setCompletedActivityIds(allCompletedActivities);

            // Determine starting position
            let foundIncomplete = false;

            if (currentTopicId) {
              // Resume from last viewed topic
              const topicIndex = flat.findIndex(t => t.topicId === currentTopicId);
              if (topicIndex !== -1) {
                setCurrentTopicIndex(topicIndex);
                const topic = flat[topicIndex];
                const sortedActs = (topic.activities || [])
                  .filter((a: Activity) => a.sortingIndex >= 0)
                  .sort((a: Activity, b: Activity) => a.sortingIndex - b.sortingIndex);

                // Find first incomplete activity in this topic
                for (let j = 0; j < sortedActs.length; j++) {
                  const activity = sortedActs[j];
                  const activityKey = `${currentTopicId}:${activity._id || activity.name}`;
                  if (!allCompletedActivities.has(activityKey)) {
                    setCurrentActivityIndex(j);
                    setInProgressActivityIndex(j);
                    foundIncomplete = true;
                    break;
                  }
                }

                // If all activities in current topic are complete, stay at first activity
                if (!foundIncomplete && sortedActs.length > 0) {
                  setCurrentActivityIndex(0);
                  setInProgressActivityIndex(null);
                  foundIncomplete = true; // Mark as found to skip the loop below
                }
              }
            }

            // If no current topic or not found, find first incomplete topic/activity
            if (!foundIncomplete) {
              for (let i = 0; i < flat.length; i++) {
                const topic = flat[i];
                const sortedActs = (topic.activities || [])
                  .filter((a: Activity) => a.sortingIndex >= 0)
                  .sort((a: Activity, b: Activity) => a.sortingIndex - b.sortingIndex);

                // Need to load activities for this topic to check completion
                try {
                  const completedIds = await getTopicCompletedActivities(courseId, topic.topicId);
                  completedIds.forEach(id => allCompletedActivities.add(`${topic.topicId}:${id}`));
                } catch (e) {
                  console.error('Error fetching completed activities for topic:', topic.topicId, e);
                }

                for (let j = 0; j < sortedActs.length; j++) {
                  const activity = sortedActs[j];
                  const activityKey = `${topic.topicId}:${activity._id || activity.name}`;
                  if (!allCompletedActivities.has(activityKey)) {
                    setCurrentTopicIndex(i);
                    setCurrentActivityIndex(j);
                    setInProgressActivityIndex(j);
                    foundIncomplete = true;
                    break;
                  }
                }
                if (foundIncomplete) break;
              }
            }

            // If all activities are completed, stay at first topic, first activity
            if (!foundIncomplete && flat.length > 0) {
              setCurrentTopicIndex(0);
              setCurrentActivityIndex(0);
              setInProgressActivityIndex(null);
            }

            // Update completedActivityIds with all loaded data
            setCompletedActivityIds(allCompletedActivities);
          }
        } catch (progressErr) {
          console.error('Error initializing course progress:', progressErr);
          // Even if progress fails, unlock first chapter
          if (chapterIds.length > 0) {
            setUnlockedChapters(new Set([chapterIds[0]]));
          }
        }

      } catch (err: any) {
        console.error('Error fetching course:', err);
        if (isCatalogMode && err.response?.status === 401) {
          logout(false);
          navigate('/', { replace: true });
          return;
        }
        setError(err.response?.data?.message || (isCatalogMode ? '加载主题目录失败' : '加载课程失败'));
      } finally {
        setLoading(false);
        // Mark initial load as done after a tick so position-saving useEffect skips the first render
        setTimeout(() => { initialLoadDoneRef.current = true; }, 0);
      }
    };

    if (courseId || isCatalogMode) {
      fetchCourse();
    }
  }, [courseId, isCatalogMode, logout, navigate, routeTopicId]);

  // Entrance animation effect
  useEffect(() => {
    if (showEntrance) {
      const statuses = [
        { progress: 20, text: '正在加载课程信息...' },
        { progress: 40, text: '正在获取学习进度...' },
        { progress: 60, text: '正在准备学习内容...' },
        { progress: 80, text: '正在初始化界面...' },
        { progress: 95, text: '即将完成...' },
      ];

      let currentIndex = 0;
      const interval = setInterval(() => {
        if (currentIndex < statuses.length) {
          setEntranceProgress(statuses[currentIndex].progress);
          setEntranceStatus(statuses[currentIndex].text);
          currentIndex++;
        }
      }, 300);

      return () => clearInterval(interval);
    }
  }, [showEntrance]);

  // Hide entrance animation when loading completes
  useEffect(() => {
    if (!loading && showEntrance) {
      setEntranceProgress(100);
      setEntranceStatus('准备完成！');
      const timer = setTimeout(() => {
        setShowEntrance(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, showEntrance]);

  // Function to load topic details on demand
  const loadTopicDetails = useCallback(async (topicId: string): Promise<Topic | null> => {
    // Check if already loaded or loading
    if (loadedTopicDetails.has(topicId)) {
      return loadedTopicDetails.get(topicId) || null;
    }
    if (loadingTopicIds.has(topicId)) {
      return null; // Already loading
    }

    try {
      setLoadingTopicIds(prev => new Set(prev).add(topicId));
      const token = useAuthStore.getState().token;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/topics/id/${topicId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const topicData = res.data.data.topic;
      setLoadedTopicDetails(prev => new Map(prev).set(topicId, topicData));

      // Also load completed activities for this topic
      if (courseId) {
        try {
          const completedIds = await getTopicCompletedActivities(courseId, topicId);
          setCompletedActivityIds(prev => {
            const newSet = new Set(prev);
            completedIds.forEach(id => newSet.add(`${topicId}:${id}`));
            return newSet;
          });
        } catch (e) {
          console.error('Error fetching completed activities for topic:', topicId, e);
        }
      }

      return topicData;
    } catch (error) {
      console.error('Error loading topic details:', error);
      if (isCatalogMode && (error as any)?.response?.status === 401) {
        logout(false);
        navigate('/', { replace: true });
      }
      return null;
    } finally {
      setLoadingTopicIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(topicId);
        return newSet;
      });
    }
  }, [loadedTopicDetails, loadingTopicIds, courseId, isCatalogMode, logout, navigate]);

  // Load topic details when current topic changes
  useEffect(() => {
    const topic = flatTopics[currentTopicIndex];
    if (topic?.topicId && !loadedTopicDetails.has(topic.topicId) && !loadingTopicIds.has(topic.topicId)) {
      loadTopicDetails(topic.topicId);
    }
  }, [currentTopicIndex, flatTopics, loadedTopicDetails, loadingTopicIds, loadTopicDetails]);

  // Enter review mode if landing on a completed topic during initial load
  useEffect(() => {
    if (!initialLoadDoneRef.current) return;
    const topic = flatTopics[currentTopicIndex];
    if (topic && completedTopics.has(topic.topicId) && !isReviewMode) {
      setIsReviewMode(true);
      setShowingSummary(true);
      const sortedActs = (topic.activities || [])
        .filter((a: Activity) => a.sortingIndex >= 0)
        .sort((a: Activity, b: Activity) => a.sortingIndex - b.sortingIndex);
      if (sortedActs.length > 0 && reviewActivityResponses.size === 0) {
        fetchReviewResponses(topic.topicId, sortedActs);
      }
    }
  }, [currentTopicIndex, flatTopics, completedTopics, initialLoadDoneRef.current]);

  // Get current topic (with merged loaded details)
  const currentTopicBase = flatTopics[currentTopicIndex];
  const currentTopic = currentTopicBase ? {
    ...currentTopicBase,
    topic: loadedTopicDetails.get(currentTopicBase.topicId) || currentTopicBase.topic
  } : undefined;

  // Get sorted activities for current topic (only enabled activities with sortingIndex >= 0)
  const getSortedActivities = useCallback((): SortedActivity[] => {
    if (!currentTopic?.activities || !Array.isArray(currentTopic.activities)) {
      return [];
    }

    return currentTopic.activities
      .map((activity, index) => ({ activity, activityIndex: index }))
      .filter(item => item.activity.sortingIndex >= 0)
      .sort((a, b) => a.activity.sortingIndex - b.activity.sortingIndex);
  }, [currentTopic]);

  const sortedActivities = getSortedActivities();
  const currentActivityItem = sortedActivities[currentActivityIndex];
  const currentActivityName = currentActivityItem?.activity?.name || null;

  // Get latest HTML content
  const getLatestHtmlContent = useCallback(() => {
    if (!currentTopic?.topic?.learningContent) return null;
    const latest = currentTopic.topic.learningContent.find(c => c.isLatest);
    return latest?.htmlContent || currentTopic.topic.learningContent[0]?.htmlContent;
  }, [currentTopic]);
  const latestHtml = getLatestHtmlContent();

  // Render LaTeX equations in HTML content using KaTeX
  const renderLatexEquations = useCallback((html: string): string => {
    if (!html) return '';

    // First, render block math equations (delimited by $$...$$)
    let processed = html.replace(/\$\$([^\$]+)\$\$/g, (match, latex) => {
      try {
        return renderToString(latex.trim(), {
          displayMode: true,
          throwOnError: false,
          strict: false,
          trust: false,
        });
      } catch (error) {
        console.error('Error rendering block math:', error);
        return match; // Return original if rendering fails
      }
    });

    // Then, render inline math equations (delimited by $...$)
    // Only match if the content contains a backslash (indicating LaTeX command)
    // This prevents regular text with parentheses from being treated as math
    processed = processed.replace(/\$([^\$\n\\]*\\[^\$\n]*?|[^\$\n]+?)\$/g, (match, latex) => {
      // Only render if it contains a backslash (LaTeX command) or common math symbols
      if (latex.includes('\\') || latex.includes('_') || latex.includes('^') || latex.includes('{')) {
        try {
          return renderToString(latex.trim(), {
            displayMode: false,
            throwOnError: false,
            strict: false,
            trust: false,
          });
        } catch (error) {
          console.error('Error rendering inline math:', error);
          return match; // Return original if rendering fails
        }
      }
      return match; // Return original if it doesn't look like LaTeX
    });

    return processed;
  }, []);

  // LatexText component for rendering text with LaTeX equations
  const LatexText: React.FC<{ text: string }> = ({ text }) => {
    if (!text) return null;
    return (
      <LatexTextSpan dangerouslySetInnerHTML={{ __html: renderLatexEquations(text) }} />
    );
  };

  // Exercise HTML for the current topic.
  //
  // `exercises[]` is VERSION HISTORY, not a list of different exercises: saving
  // appends a version and clears isLatest on the rest. Taking exercises[0] used
  // to serve a stale copy - in 8 topics the newer version had a tracking fix the
  // served one lacked. pickLatestExercise resolves the live version and falls
  // back sensibly for records written before versioning existed.
  const exerciseContent = useMemo(() => {
    const exercises = currentTopic?.topic?.exercises;
    if (!exercises || exercises.length === 0) {
      console.log('[Exercise] No exercises found for topic:', currentTopic?.topic?.title);
      return null;
    }

    const latest = pickLatestExercise(exercises as any);
    const html = readExerciseHtml(latest);

    if (!html) {
      console.log('[Exercise] No valid content in the latest exercise version');
      return null;
    }
    console.log(
      '[Exercise] Using version:', latest?.version || '(unversioned)',
      '| isLatest:', latest?.isLatest === true,
      '| of', exercises.length, 'version(s), length:', html.length
    );
    return html;
  }, [currentTopic?.topic?.exercises, currentTopic?.topic?.title]);

  // Check if topic has exercise content
  const hasExercise = useMemo(() => {
    return exerciseContent !== null;
  }, [exerciseContent]);

  const externalReading = currentActivityName === 'reading' && /^https?:\/\//.test(currentActivityItem?.activity?.link || '');
  const presentedRevision = currentActivityName === 'reading' && !externalReading
    ? (currentTopic?.topic?.learningContent?.find(c => c.isLatest) || currentTopic?.topic?.learningContent?.[0])?._id
    : (currentActivityName === 'exercise' || currentActivityName === 'quiz')
      ? pickLatestExercise(currentTopic?.topic?.exercises || [])?._id : undefined;
  const timingVisitKey = useMemo(() => timingId(), [user?.id, courseId, currentTopic?.topicId,
    currentActivityItem?.activity?._id, presentedRevision]);
  const [readyTimingKey, setReadyTimingKey] = useState('');
  const timingVisitRef = useRef(timingVisitKey);
  timingVisitRef.current = timingVisitKey;
  const needsFrameReady = currentActivityName === 'video' ||
    (currentActivityName === 'reading' && (externalReading || /<(script|style|button)[\s>]/i.test(latestHtml || ''))) ||
    ((currentActivityName === 'exercise' || currentActivityName === 'quiz') && /<(script|style|button)[\s>]/i.test(exerciseContent || ''));
  const timingContentPresent = currentActivityName === 'reading' ? !!(latestHtml || currentTopic?.topic?.description || currentActivityItem?.activity?.link)
    : currentActivityName === 'exercise' || currentActivityName === 'quiz' ? hasExercise
    : currentActivityName === 'survey' ? !!currentSurvey?.htmlContent && currentSurvey._id === currentActivityItem?.activity?.value
    : currentActivityName === 'video' ? !!(currentActivityItem?.activity?.link || currentActivityItem?.activity?.value) : currentActivityName === 'selfExplanation';
  const timingVisible = !isCatalogMode && course?._id === courseId && !loading && !showEntrance && !showingSummary &&
    !showMasteryCelebration && !showRelearnPrompt;
  useForegroundTiming(timingVisible && currentTopic && currentActivityItem?.activity?._id ? {
    context: 'course', courseId, chapterId: currentTopic.chapterId, sectionId: currentTopic.sectionId,
    topicId: currentTopic.topicId, activityId: currentActivityItem.activity._id,
    surface: currentActivityName as TimingSurface
  } : null, timingContentPresent && (!needsFrameReady || readyTimingKey === timingVisitKey),
  presentedRevision || null, currentActivityName === 'review');

  // Listen for exercise responses from iframe
  // Using refs to keep the handler stable (never detached/reattached) so no messages are lost.
  // The previous approach re-attached the listener on every currentTopic change (which
  // re-renders often), creating a window where postMessages could be dropped.

  // Refs that the stable handler reads from
  const courseIdRef = useRef(courseId);
  const currentTopicRef = useRef(currentTopic);
  const currentActivityItemRef = useRef(currentActivityItem);
  const isCatalogModeRef = useRef(isCatalogMode);
  courseIdRef.current = courseId;
  currentTopicRef.current = currentTopic;
  currentActivityItemRef.current = currentActivityItem;
  isCatalogModeRef.current = isCatalogMode;

  // Per-question rows posted by the exercise HTML as an `exerciseResponses` batch.
  //
  // These are the only rows carrying item-level detail (selectedParameterId,
  // correctAnswer, per-question totalTimeSpent) under ids like `<id>-q1`. They
  // used to be dropped: the batch went into React state, and handleExerciseComplete
  // then replaced that state with whatever the iframe returned for
  // `getExerciseResponses` - a bare-`<id>` row built by the injected
  // reportExerciseAnswer shim. Production evidence: stored documents held one
  // bare-id row and no `-q1` row at all.
  //
  // Keeping them in a ref (not state) means the stable message handler sees them
  // without being re-attached, and they survive until the topic changes.
  const itemRowsRef = useRef<Map<string, ExerciseResponse>>(new Map());

  // Mirrors completedActivityIds so the stable handler can read it without
  // re-subscribing. Used to avoid downgrading an already-completed activity
  // back to isCompleted:false when a later batch arrives.
  const completedActivityIdsRef = useRef(completedActivityIds);
  completedActivityIdsRef.current = completedActivityIds;

  // Field-wise merge that never lets an absent field erase a present one.
  const mergeRow = (base: any, extra: any) => {
    const out = { ...(base || {}) };
    Object.entries(extra || {}).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out as ExerciseResponse;
  };

  // Record an incoming batch and persist it right away.
  //
  // Persisting on receipt (rather than only from handleExerciseComplete) matters
  // because that handler returns early unless mastery === 'mastered'. A student
  // who never reaches three consecutive correct answers would otherwise leave no
  // item-level rows behind at all. The server merges responses by exerciseId, so
  // repeated saves accumulate instead of overwriting.
  const recordItemRows = useCallback(async (rows: ExerciseResponse[]) => {
    if (!Array.isArray(rows) || rows.length === 0) return;

    rows.forEach((r) => {
      if (!r?.exerciseId) return;
      const prev = itemRowsRef.current.get(r.exerciseId);
      itemRowsRef.current.set(r.exerciseId, prev ? mergeRow(prev, r) : r);
    });

    const cId = courseIdRef.current;
    const cTopic = currentTopicRef.current;
    const cActivity = currentActivityItemRef.current?.activity;
    if (isCatalogModeRef.current) {
      return;
    }
    if (!cId || !cTopic || !cActivity) {
      console.warn('[Exercise] Item rows held but not persisted - missing context');
      return;
    }

    const activityId = cActivity._id || cActivity.name;
    try {
      await saveExerciseResponse(cId, cTopic.topicId, activityId, {
        activityType: cActivity.name === 'quiz' ? 'quiz' : 'exercise',
        chapterId: cTopic.chapterId,
        sectionId: cTopic.sectionId,
        responses: Array.from(itemRowsRef.current.values()),
        isCompleted: completedActivityIdsRef.current.has(`${cTopic.topicId}:${activityId}`)
      });
      console.log('[Exercise] Persisted', itemRowsRef.current.size, 'item row(s)');
    } catch (error) {
      console.error('[Exercise] Failed to persist item rows:', error);
    }
  }, []);

  // Shared helper: deduplicate and save an exercise attempt
  const saveAttemptWithDedup = useCallback(async (
    attempt: ExerciseAttempt,
    source: string
  ) => {
    const attemptVisit = timingVisitRef.current;
    const cId = courseIdRef.current;
    const cTopic = currentTopicRef.current;
    const cActivity = currentActivityItemRef.current?.activity;

    if (isCatalogModeRef.current) {
      setMasteryStatus((previous) => {
        const consecutiveCorrect = attempt.isCorrect ? previous.consecutiveCorrect + 1 : 0;
        const consecutiveIncorrect = attempt.isCorrect ? 0 : previous.consecutiveIncorrect + 1;
        const masteryStatus = consecutiveCorrect >= 3
          ? 'mastered'
          : consecutiveIncorrect >= 3
            ? 'needs_review'
            : 'in_progress';
        return {
          masteryStatus,
          consecutiveCorrect,
          consecutiveIncorrect,
          totalAttempts: previous.totalAttempts + 1
        };
      });
      return;
    }

    console.log(`[Mastery] Processing attempt from ${source}:`, {
      exerciseId: attempt.exerciseId,
      isCorrect: attempt.isCorrect,
      hasCourse: !!cId,
      hasTopic: !!cTopic,
      hasActivity: !!cActivity
    });

    if (cId && cTopic && cActivity) {
      try {
        const activityId = cActivity._id || cActivity.name;
        const result = await saveExerciseAttempt(
          cId,
          cTopic.topicId,
          activityId,
          attempt,
          {
            chapterId: cTopic.chapterId,
            sectionId: cTopic.sectionId,
            activityType: cActivity.name === 'quiz' ? 'quiz' : 'exercise'
          }
        );
        if (timingVisitRef.current !== attemptVisit) return;

        console.log(`[Mastery] Attempt saved from ${source}, mastery:`, result.data.mastery);
        setMasteryStatus(result.data.mastery);

        if (result.data.mastery.masteryStatus === 'mastered') {
          setShowMasteryCelebration(true);
          toast.success('恭喜你掌握了这个主题！');
        } else if (result.data.mastery.masteryStatus === 'needs_review') {
          setShowRelearnPrompt(true);
          toast.error('建议重新学习该主题内容');
        }
      } catch (error) {
        console.error(`[Mastery] Failed to save attempt from ${source}:`, error);
      }
    } else {
      console.warn(`[Mastery] Missing context for ${source}:`, {
        courseId: !!cId,
        topic: !!cTopic,
        activity: !!cActivity
      });
    }
  }, []);

  // Stable message handler - attached once, never detached
  useEffect(() => {
    const handleExerciseMessage = async (event: MessageEvent) => {
      // Handle single exercise attempt (immediate save for mastery tracking)
      const frame = document.querySelector<HTMLIFrameElement>(`iframe[data-learning-visit="${timingVisitRef.current}"]`);
      if (!frame || event.source !== frame.contentWindow || event.origin !== window.location.origin ||
        !['exercise', 'quiz'].includes(currentActivityItemRef.current?.activity?.name || '')) return;
      const receivedVisit = timingVisitRef.current;
      if (event.data?.type === 'exerciseAttempt') {
        const attempt = event.data.payload as ExerciseAttempt;
        console.log('[Mastery] Received exerciseAttempt:', attempt);
        await saveAttemptWithDedup(attempt, 'exerciseAttempt');
        if (timingVisitRef.current !== receivedVisit) return;

        // Also update local state
        setExerciseResponses(prev => {
          const existingIndex = prev.findIndex(r => r.exerciseId === attempt.exerciseId);
          const response: ExerciseResponse = {
            exerciseId: attempt.exerciseId,
            exerciseOrder: attempt.exerciseOrder,
            questionType: attempt.questionType,
            questionText: attempt.questionText,
            selectedParameterId: attempt.selectedParameterId,
            selectedParameter: attempt.selectedParameter,
            finalAnswer: attempt.finalAnswer,
            correctAnswer: attempt.correctAnswer,
            isCorrect: attempt.isCorrect,
            isCompleted: true,
            totalTimeSpent: attempt.totalTimeSpent
          };
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = response;
            return updated;
          }
          return [...prev, response];
        });
      }

      // Handle exercise data from iframe
      if (event.data?.type === 'exerciseResponse') {
        const payload = event.data.payload;
        console.log('[Mastery] Received exerciseResponse:', payload);

        // Check if this is actually an exercise attempt (has isCorrect field)
        // Old exercise HTML sends exerciseResponse instead of exerciseAttempt
        if (payload && payload.isCorrect !== undefined && payload.finalAnswer !== undefined) {
          const attempt = payload as ExerciseAttempt;
          await saveAttemptWithDedup(attempt, 'exerciseResponse');
          if (timingVisitRef.current !== receivedVisit) return;
        }

        setExerciseResponses(prev => {
          const existingIndex = prev.findIndex(r => r.exerciseId === event.data.payload.exerciseId);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = event.data.payload;
            return updated;
          }
          return [...prev, event.data.payload];
        });
      }

      // Handle all exercise responses at once (batch submit)
      if (event.data?.type === 'exerciseResponses') {
        console.log('[Mastery] Received exerciseResponses batch:', event.data.payload);
        const rows = (event.data.payload || []) as ExerciseResponse[];
        setExerciseResponses(rows);
        await recordItemRows(rows);
      }

      // Handle exercise_complete message from older exercise HTML files
      if (event.data?.type === 'exercise_complete') {
        console.log('[Mastery] Received exercise_complete:', event.data.data);
        const reportData = event.data.data;

        if (reportData.responses) {
          const allCorrect = reportData.allCorrect === true ||
            (reportData.totalQuestions > 0 && reportData.correctCount === reportData.totalQuestions);

          const attempt: ExerciseAttempt = {
            exerciseId: reportData.exerciseId || (reportData.responses[0]?.exercise_id),
            questionType: 'parameterized_choice',
            questionText: reportData.responses.map((r: any) => r.question_text || '').join('; '),
            finalAnswer: reportData.responses.map((r: any) => r.user_answer),
            correctAnswer: reportData.responses.map((r: any) => r.correct_answer),
            isCorrect: allCorrect,
            totalTimeSpent: reportData.totalTimeSpent || 0
          };

          await saveAttemptWithDedup(attempt, 'exercise_complete');
        }
      }
    };

    window.addEventListener('message', handleExerciseMessage);
    return () => window.removeEventListener('message', handleExerciseMessage);
  }, [saveAttemptWithDedup, recordItemRows]);

  // Reset exercise responses when topic changes
  useEffect(() => {
    setExerciseResponses([]);
    itemRowsRef.current = new Map();
  }, [timingVisitKey]);

  useEffect(() => {
    if (!isCatalogMode) return;
    if (masteryStatus.masteryStatus === 'mastered') {
      setShowMasteryCelebration(true);
      toast.success('恭喜你掌握了这个主题！');
    } else if (masteryStatus.masteryStatus === 'needs_review') {
      setShowRelearnPrompt(true);
      toast.error('建议重新学习该主题内容');
    }
  }, [isCatalogMode, masteryStatus.masteryStatus]);

  // Save current topic position to backend so refresh restores the correct page.
  // Depend only on primitive topicId/chapterId (not the currentTopic object, which is
  // a new reference on every render) and dedupe via a ref so this only fires when the
  // resolved position actually changes for the current course.
  const savedTopicId = currentTopic?.topicId;
  const savedChapterId = currentTopic?.chapterId;
  const lastSavedPositionRef = useRef<{ courseId: string; topicId: string; chapterId?: string } | null>(null);

  useEffect(() => {
    if (isCatalogMode || !initialLoadDoneRef.current || !courseId || !savedTopicId) return;

    const last = lastSavedPositionRef.current;
    if (last && last.courseId === courseId && last.topicId === savedTopicId && last.chapterId === savedChapterId) {
      return;
    }

    lastSavedPositionRef.current = { courseId, topicId: savedTopicId, chapterId: savedChapterId };
    updateCurrentPosition(courseId, savedTopicId, savedChapterId).catch(err => {
      console.error('Failed to save current position:', err);
      // Allow a retry on the next render if this save failed
      if (lastSavedPositionRef.current &&
          lastSavedPositionRef.current.courseId === courseId &&
          lastSavedPositionRef.current.topicId === savedTopicId &&
          lastSavedPositionRef.current.chapterId === savedChapterId) {
        lastSavedPositionRef.current = null;
      }
    });
  }, [currentTopicIndex, courseId, savedTopicId, savedChapterId, isCatalogMode]);

  // Fetch mastery status when exercise activity loads
  const currentTopicId = currentTopic?.topicId;
  const currentActivityId = currentActivityItem?.activity?._id || currentActivityItem?.activity?.name;

  useEffect(() => {
    const masteryVisit = timingVisitRef.current;
    const fetchMasteryStatus = async () => {
      if (isCatalogMode || !['exercise', 'quiz'].includes(currentActivityName || '') || !courseId || !currentTopicId || !currentActivityId) {
        return;
      }

      try {
        const status = await getExerciseMastery(courseId, currentTopicId, currentActivityId);
        if (timingVisitRef.current !== masteryVisit) return;
        setMasteryStatus(status);
      } catch (error) {
        console.error('Failed to fetch mastery status:', error);
      }
    };

    fetchMasteryStatus();
  }, [currentActivityName, courseId, currentTopicId, currentActivityId, isCatalogMode]);

  // Check if current activity is completed when activity changes
  // Use ref to track if we've already loaded self-explanation for this activity
  const loadedSelfExplanationRef = useRef<string | null>(null);

  useEffect(() => {
    const topicId = flatTopics[currentTopicIndex]?.topicId;
    if (!topicId || !currentActivityItem?.activity) return;

    const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
    const activityKey = `${topicId}:${activityId}`;
    const isActivityCompleted = completedActivityIds.has(activityKey);

    // Set readingCompleted based on database state
    if (currentActivityName === 'reading') {
      setReadingCompleted(isActivityCompleted);
    }

    // Load existing self-explanation if this is a selfExplanation activity
    // Only load once per activity (track using ref to avoid resetting while user types)
    if (currentActivityName === 'selfExplanation') {
      // Check if we already loaded for this activity
      if (loadedSelfExplanationRef.current === activityKey) {
        return; // Already loaded, don't reset
      }
      loadedSelfExplanationRef.current = activityKey;

      // In review mode, prefer cached response data for pre-fill
      if (isReviewMode && reviewActivityResponses.has(activityKey)) {
        const cached = reviewActivityResponses.get(activityKey);
        if (cached?.selfExplanationData) {
          setExplanation(cached.selfExplanationData.explanation || '');
          setUnderstandingLevel(cached.selfExplanationData.understandingLevel || null);
          return;
        }
      }

      const loadExistingSelfExplanation = async () => {
        try {
          const token = useAuthStore.getState().token;
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/self-explanations?topic=${topicId}&cardId=course-content&limit=1`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data?.data?.explanations?.length > 0) {
            const latestExplanation = response.data.data.explanations[0];
            setExplanation(latestExplanation.explanation || '');
            setUnderstandingLevel(latestExplanation.understanding || null);
          } else {
            // No existing explanation, reset to empty
            setExplanation('');
            setUnderstandingLevel(null);
          }
        } catch (err: any) {
          console.error('Error loading self-explanation:', err);
          if (isCatalogMode && err.response?.status === 401) {
            logout(false);
            navigate('/', { replace: true });
            return;
          }
          setExplanation('');
          setUnderstandingLevel(null);
        }
      };
      loadExistingSelfExplanation();
    } else {
      // Reset explanation state when switching to non-selfExplanation activity
      setExplanation('');
      setUnderstandingLevel(null);
      loadedSelfExplanationRef.current = null;
    }
  }, [currentTopicIndex, currentActivityIndex, flatTopics, currentActivityItem, currentActivityName, completedActivityIds, isCatalogMode, logout, navigate]);

  // Calculate time spent in current activity (in seconds)

  // Save activity completion to backend
  const saveActivityCompletion = useCallback(async (
    activityType: 'reading' | 'video' | 'selfExplanation' | 'survey' | 'review' | 'exercise',
    additionalData?: any
  ) => {
    if (isCatalogMode) {
      return;
    }
    // Check that we have the required data - activity must have either _id or name
    if (!courseId || !currentTopic?.topicId || !currentActivityItem?.activity) {
      return;
    }
    const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
    if (!activityId) {
      return;
    }

    try {
      const baseParams = {
        courseId,
        topicId: currentTopic.topicId,
        activityId,
        chapterId: currentTopic.chapterId,
        sectionId: currentTopic.sectionId,
        isCompleted: true
      };

      switch (activityType) {
        case 'reading':
          await saveReadingProgress(courseId, currentTopic.topicId, activityId, {
            ...baseParams,
            ...additionalData
          });
          break;
        case 'video':
          await saveVideoProgress(courseId, currentTopic.topicId, activityId, {
            ...baseParams,
            videoUrl: currentActivityItem.activity.value,
            ...additionalData
          });
          break;
        case 'selfExplanation':
          await saveSelfExplanation(courseId, currentTopic.topicId, activityId, {
            ...baseParams,
            ...additionalData
          });
          break;
        case 'survey':
          await saveSurveyResponse(courseId, currentTopic.topicId, activityId, {
            ...baseParams,
            surveyId: currentActivityItem.activity.value,
            ...additionalData
          });
          break;
        case 'exercise':
          // Exercise is handled separately in handleExerciseComplete
          // which calls saveExerciseResponse directly with responses
          break;
      }

      // Update progress from response if available
      if (courseProgress) {
        const progress = await getCourseProgress(courseId);
        setCourseProgress(progress);
        setOverallProgress(progress.progressPercentage);
      }
    } catch (err) {
      console.error('Error saving activity completion:', err);
    }
  }, [courseId, currentTopic, currentActivityItem, courseProgress, isCatalogMode]);

  // Navigation handlers
  const goToTopic = async (index: number) => {
    if (index >= 0 && index < flatTopics.length) {
      const topic = flatTopics[index];

      // Check if the chapter is unlocked
      if (!isCatalogMode && !unlockedChapters.has(topic.chapterId)) {
        toast.error('请先完成当前章节的学习');
        return;
      }

      if (isCatalogMode) {
        setExpandedChapters(previous => new Set(previous).add(topic.chapterId));
        if (topic.sectionId) {
          setExpandedSections(previous => new Set(previous).add(topic.sectionId!));
        }
      }

      // Load topic details on demand if not already loaded
      if (!loadedTopicDetails.has(topic.topicId)) {
        await loadTopicDetails(topic.topicId);
      }

      const sortedActs = (topic.activities || [])
        .filter((a: Activity) => a.sortingIndex >= 0)
        .sort((a: Activity, b: Activity) => a.sortingIndex - b.sortingIndex);

      const isTopicCompleted = completedTopics.has(topic.topicId);

      if (isTopicCompleted) {
        // Completed topic: enter review mode, show summary card
        setIsReviewMode(true);
        setShowingSummary(true);
        setCurrentTopicIndex(index);
        setCurrentActivityIndex(0);
        setInProgressActivityIndex(null);

        // Fetch activity responses for summary card
        fetchReviewResponses(topic.topicId, sortedActs);
      } else {
        // Incomplete topic: normal mode
        setIsReviewMode(false);
        setShowingSummary(false);

        // Find first incomplete activity in the new topic
        let firstIncompleteIdx = 0;
        for (let j = 0; j < sortedActs.length; j++) {
          const activity = sortedActs[j];
          const activityId = activity._id || activity.name;
          const activityKey = `${topic.topicId}:${activityId}`;
          if (!completedActivityIds.has(activityKey)) {
            firstIncompleteIdx = j;
            break;
          }
          // If all completed, default to last activity
          firstIncompleteIdx = sortedActs.length - 1;
        }

        setCurrentTopicIndex(index);
        setCurrentActivityIndex(firstIncompleteIdx);
        setInProgressActivityIndex(firstIncompleteIdx);
      }

      setExplanation('');
      setUnderstandingLevel(null);
      setReadingCompleted(false);
      setCurrentSurvey(null);
      setMasteryStatus({
        masteryStatus: 'in_progress',
        consecutiveCorrect: 0,
        consecutiveIncorrect: 0,
        totalAttempts: 0
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevTopic = () => goToTopic(currentTopicIndex - 1);
  const goToNextTopic = () => goToTopic(currentTopicIndex + 1);

  // Fetch activity responses for the summary card
  const fetchReviewResponses = useCallback(async (topicId: string, sortedActs: Activity[]) => {
    if (isCatalogMode || !courseId) return;
    const newMap = new Map<string, any>();
    for (const act of sortedActs) {
      const activityId = act._id || act.name;
      const activityKey = `${topicId}:${activityId}`;
      try {
        const response = await getActivityResponse(courseId, topicId, activityId);
        if (response) {
          newMap.set(activityKey, response);
        }
      } catch (err) {
        console.error('Error fetching response for activity:', activityId, err);
      }
    }
    setReviewActivityResponses(newMap);
  }, [courseId, isCatalogMode]);

  const goBackToSummary = () => {
    setShowingSummary(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Activity navigation
  const goToActivity = (activityIndex: number) => {
    if (activityIndex >= 0 && activityIndex < sortedActivities.length) {
      if (isReviewMode) {
        setShowingSummary(false);
      } else {
        const targetActivity = sortedActivities[activityIndex];
        const activityId = targetActivity.activity._id || targetActivity.activity.name;
        const activityKey = `${currentTopic?.topicId}:${activityId}`;
        const isTargetCompleted = completedActivityIds.has(activityKey);
        if (!isTargetCompleted || inProgressActivityIndex === null) {
          setInProgressActivityIndex(activityIndex);
        }
      }

      setCurrentActivityIndex(activityIndex);
      setReadingCompleted(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextActivity = () => {
    if (currentActivityIndex < sortedActivities.length - 1) {
      goToActivity(currentActivityIndex + 1);
    } else {
      // All activities completed, mark topic as completed and go to next topic
      markTopicCompleted();
    }
  };

  const goToPrevActivity = () => {
    if (currentActivityIndex > 0) {
      goToActivity(currentActivityIndex - 1);
    }
  };

  const toggleChapter = (chapterId: string) => {
    setExpandedChapters(prev => {
      const next = new Set(prev);
      if (next.has(chapterId)) {
        next.delete(chapterId);
      } else {
        next.add(chapterId);
      }
      return next;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(previous => {
      const next = new Set(previous);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Reading activity completion handler
  const handleReadingCheckboxChange = async (checked: boolean) => {
    setReadingCompleted(checked);
    if (checked) {
      // Save to database immediately when checked
      await saveActivityCompletion('reading', {
        scrollDepth: 100
      });
      // Update completedActivityIds
      if (currentTopic && currentActivityItem?.activity) {
        const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
        const activityKey = `${currentTopic.topicId}:${activityId}`;
        setCompletedActivityIds(prev => new Set(prev).add(activityKey));
      }
    }
  };

  const handleReadingComplete = async () => {
    // Reading is already saved when checkbox was checked, just navigate
    goToNextActivity();
  };

  // Video activity completion handler
  const handleVideoComplete = async () => {
    await saveActivityCompletion('video', {
    });
    // Update completedActivityIds
    if (currentTopic && currentActivityItem?.activity) {
      const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
      const activityKey = `${currentTopic.topicId}:${activityId}`;
      setCompletedActivityIds(prev => new Set(prev).add(activityKey));
    }
    goToNextActivity();
  };

  // Survey activity completion handler
  const handleSurveyComplete = async () => {
    await saveActivityCompletion('survey', {});
    // Update completedActivityIds
    if (currentTopic && currentActivityItem?.activity) {
      const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
      const activityKey = `${currentTopic.topicId}:${activityId}`;
      setCompletedActivityIds(prev => new Set(prev).add(activityKey));
    }
    goToNextActivity();
  };

  // Exercise activity completion handler
  const handleExerciseComplete = async () => {
    console.log('handleExerciseComplete called');
    console.log('Current masteryStatus:', masteryStatus);

    // Check if student has mastered the topic
    if (masteryStatus.masteryStatus !== 'mastered') {
      toast.error('请先完成练习并掌握该主题（连续答对3题）');
      return;
    }

    console.log('Current exerciseResponses:', exerciseResponses);

    // Request exercise responses from iframe before saving
    const completionVisit = timingVisitRef.current;
    const iframes = document.querySelectorAll<HTMLIFrameElement>(`iframe[data-learning-visit="${completionVisit}"]`);
    console.log('Found iframes:', iframes.length);

    // Create a promise that resolves when we get responses or timeout
    const responsesPromise = new Promise<ExerciseResponse[]>((resolve) => {
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', handleMessage);
        console.log('Timeout waiting for exercise responses');
        resolve(exerciseResponses);
      }, 1000);

      const handleMessage = (event: MessageEvent) => {
        const frame = document.querySelector<HTMLIFrameElement>(`iframe[data-learning-visit="${timingVisitRef.current}"]`);
        if (frame && event.source === frame.contentWindow && event.origin === window.location.origin &&
          event.data?.type === 'exerciseResponses') {
          console.log('Received exerciseResponses from iframe:', event.data.payload);
          clearTimeout(timeoutId);
          window.removeEventListener('message', handleMessage);
          resolve(event.data.payload || []);
        }
      };

      window.addEventListener('message', handleMessage);

      // Send message to iframe to get all responses
      iframes.forEach((iframe) => {
        iframe.contentWindow?.postMessage({ type: 'getExerciseResponses' }, '*');
      });
    });

    // Wait for responses or timeout
    const iframeResponses = await responsesPromise;
    if (completionVisit !== timingVisitRef.current) return;

    // Union the iframe's reply with the per-question rows collected from the
    // exercise's own `exerciseResponses` batches. The two use different ids
    // (bare `<id>` from the injected shim vs `<id>-q1` / `<id>-summary` from the
    // exercise), so replacing one with the other silently dropped item detail.
    const byId = new Map<string, ExerciseResponse>();
    itemRowsRef.current.forEach((row, id) => byId.set(id, row));
    (iframeResponses || []).forEach((row) => {
      if (!row?.exerciseId) return;
      const existing = byId.get(row.exerciseId);
      byId.set(row.exerciseId, existing ? mergeRow(row, existing) : row);
    });
    const finalResponses = Array.from(byId.values());
    console.log('Final responses to save:', finalResponses);

    // Save exercise responses with parameters and answers
    if (courseId && currentTopic && currentActivityItem?.activity) {
      const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
      console.log('Saving with:', {
        courseId,
        topicId: currentTopic.topicId,
        activityId,
        responsesCount: finalResponses.length,
        finalResponses
      });

      try {
        const result = await saveExerciseResponse(
          courseId,
          currentTopic.topicId,
          activityId,
          {
            chapterId: currentTopic.chapterId,
            sectionId: currentTopic.sectionId,
            responses: finalResponses,
            activityType: currentActivityName === 'quiz' ? 'quiz' : 'exercise',
            isCompleted: true
          }
        );
        console.log('Exercise responses saved successfully:', result);
        toast.success('练习已完成');
      } catch (error: any) {
        console.error('Failed to save exercise responses:', error);
        console.error('Error details:', error?.response?.data || error?.message);
        toast.error('保存练习失败: ' + (error?.response?.data?.message || error?.message || '未知错误'));
      }
    } else {
      console.log('Missing required data:', { courseId, currentTopic, currentActivityItem });
    }

    // Update completedActivityIds
    if (currentTopic && currentActivityItem?.activity) {
      const activityKey = `${currentTopic.topicId}:${currentActivityItem.activity._id || currentActivityItem.activity.name}`;
      setCompletedActivityIds(prev => new Set(prev).add(activityKey));
    }

    goToNextActivity();
  };

  // Self-explanation submit
  const handleSelfExplanationSubmit = async () => {
    if (!explanation.trim()) {
      toast.error('请先填写你的解释');
      return;
    }

    if (!understandingLevel) {
      toast.error('请选择你的理解程度');
      return;
    }

    try {
      const token = useAuthStore.getState().token;

      // Save to original self-explanations collection (for backward compatibility)
      await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/self-explanations`,
        {
          topic: currentTopic?.topicId,
          cardId: 'course-content',
          explanation: explanation,
          understanding: understandingLevel,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Save to new student activities collection
      await saveActivityCompletion('selfExplanation', {
        explanation: explanation,
        understandingLevel: understandingLevel
      });

      // Update completedActivityIds
      if (currentTopic && currentActivityItem?.activity) {
        const activityId = currentActivityItem.activity._id || currentActivityItem.activity.name;
        const activityKey = `${currentTopic.topicId}:${activityId}`;
        setCompletedActivityIds(prev => new Set(prev).add(activityKey));
      }

      toast.success('解释已保存！');

      trackSelfExplanationSubmit({
        topicId: currentTopic?.topicId || '',
        cardId: 'course-content',
        explanationLength: explanation.length,
        understandingLevel: understandingLevel,
      });

      // Move to next activity
      goToNextActivity();
    } catch (err: any) {
      console.error('Error saving explanation:', err);
      if (isCatalogMode && err.response?.status === 401) {
        logout(false);
        navigate('/', { replace: true });
        return;
      }
      toast.error('保存失败');
    }
  };

  // Save self-explanation edit in review mode
  const handleSelfExplanationReviewSave = async () => {
    if (!explanation.trim() || !understandingLevel) return;

    try {
      if (isCatalogMode) {
        const token = useAuthStore.getState().token;
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/self-explanations`,
          {
            topic: currentTopic?.topicId,
            cardId: 'course-content',
            explanation,
            understanding: understandingLevel,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('修改已保存！');
        goBackToSummary();
        return;
      }

      const activityId = currentActivityItem?.activity?._id || currentActivityItem?.activity?.name;
      const activityKey = `${currentTopic?.topicId}:${activityId}`;
      const existingResponse = reviewActivityResponses.get(activityKey);
      const previousEditCount = existingResponse?.selfExplanationData?.editCount || 0;

      await saveActivityResponse({
        courseId: courseId!,
        topicId: currentTopic!.topicId,
        activityId: activityId!,
        activityType: 'selfExplanation',
        chapterId: currentTopic?.chapterId,
        sectionId: currentTopic?.sectionId,
        isCompleted: true,
        selfExplanationData: {
          explanation: explanation,
          understandingLevel: understandingLevel,
          wordCount: explanation.split(/\s+/).filter((w: string) => w).length,
          edited: true,
          editCount: previousEditCount + 1
        }
      });

      // Update cached response
      setReviewActivityResponses(prev => {
        const newMap = new Map(prev);
        newMap.set(activityKey, {
          ...prev.get(activityKey),
          selfExplanationData: {
            explanation,
            understandingLevel,
            wordCount: explanation.split(/\s+/).filter((w: string) => w).length,
            edited: true,
            editCount: previousEditCount + 1
          }
        });
        return newMap;
      });

      toast.success('修改已保存！');
      goBackToSummary();
    } catch (err: any) {
      console.error('Error saving explanation edit:', err);
      if (isCatalogMode && err.response?.status === 401) {
        logout(false);
        navigate('/', { replace: true });
        return;
      }
      toast.error('保存失败');
    }
  };

  // Mark topic as completed
  const markTopicCompleted = async () => {
    const topicId = currentTopic?.topicId;
    const chapterId = currentTopic?.chapterId;
    const sectionId = currentTopic?.sectionId;

    if (topicId && isCatalogMode) {
      const newCompletedTopics = new Set(completedTopics);
      newCompletedTopics.add(topicId);
      setCompletedTopics(newCompletedTopics);
      setOverallProgress(flatTopics.length > 0
        ? Math.round((newCompletedTopics.size / flatTopics.length) * 100)
        : 0);
    } else if (topicId && courseId) {
      try {
        // Save to database
        await markTopicCompletedApi(courseId, topicId, chapterId, sectionId);

        // Update local state
        const newCompletedTopics = new Set(completedTopics);
        newCompletedTopics.add(topicId);
        setCompletedTopics(newCompletedTopics);
        setOverallProgress(Math.round((newCompletedTopics.size / flatTopics.length) * 100));

        // Check if all topics in current chapter are completed
        if (chapterId) {
          const chapterTopics = flatTopics.filter(t => t.chapterId === chapterId);
          const allChapterTopicsCompleted = chapterTopics.length > 0 &&
            chapterTopics.every(t => newCompletedTopics.has(t.topicId));

          if (allChapterTopicsCompleted) {
            // Find next chapter and unlock it
            const chapters = course?.chapters || [];
            const currentChapterIndex = chapters.findIndex((c: any) => c._id === chapterId);

            if (currentChapterIndex >= 0 && currentChapterIndex < chapters.length - 1) {
              const nextChapter = chapters[currentChapterIndex + 1];
              setUnlockedChapters(prev => {
                const next = new Set(prev);
                next.add(nextChapter._id);
                return next;
              });
              // Expand the newly unlocked chapter
              setExpandedChapters(prev => {
                const next = new Set(prev);
                next.add(nextChapter._id);
                return next;
              });
              toast.success('🎉 恭喜完成本章！下一章已解锁');
            }
          }
        }
      } catch (err) {
        console.error('Error marking topic as completed:', err);
        toast.error('保存完成状态失败');
      }
    }

    // Auto go to next topic or show completion
    if (currentTopicIndex < flatTopics.length - 1) {
      goToNextTopic();
    } else {
      toast.success('恭喜你完成了本课程的学习！');
    }
  };

  // Survey handlers
  const fetchSurveyContent = useCallback(async (surveyId: string) => {
    if (!surveyId) return null;
    try {
      const token = useAuthStore.getState().token;
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/surveys/${surveyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data.survey;
    } catch (err) {
      console.error('Error fetching survey:', err);
      return null;
    }
  }, []);

  // Load survey content when activity changes to survey
  useEffect(() => {
    if (currentActivityName === 'survey' && currentActivityItem?.activity?.value) {
      const surveyId = currentActivityItem.activity.value;
      if (surveyId && (!currentSurvey || currentSurvey._id !== surveyId)) {
        fetchSurveyContent(surveyId).then(survey => {
          if (survey) {
            setCurrentSurvey(survey);
          }
        });
      }
    }
  }, [currentActivityName, currentActivityItem, currentSurvey, fetchSurveyContent]);

  // Loading state - show entrance animation instead of simple loading text
  if (loading) {
    return (
      <PageContainer>
        <EntranceOverlay $visible={true}>
          <EntranceContent>
            <EntranceIcon>📚</EntranceIcon>
            <EntranceTitle>正在打开课程</EntranceTitle>
            <EntranceSubtitle>请稍候...</EntranceSubtitle>
            <EntranceProgressBar>
              <EntranceProgressFill $progress={entranceProgress} />
            </EntranceProgressBar>
            <EntranceStatus>{entranceStatus}</EntranceStatus>
          </EntranceContent>
        </EntranceOverlay>
      </PageContainer>
    );
  }

  // Error state
  if (error || !course) {
    return (
      <PageContainer>
        <ErrorContainer>{error || '课程未找到'}</ErrorContainer>
      </PageContainer>
    );
  }

  if (isCatalogMode && flatTopics.length === 0) {
    return (
      <PageContainer>
        <ErrorContainer>暂无可学习的主题</ErrorContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Entrance Animation Overlay */}
      <EntranceOverlay $visible={showEntrance}>
        <EntranceContent>
          <EntranceIcon>📚</EntranceIcon>
          <EntranceTitle>{course?.title || '正在加载课程'}</EntranceTitle>
          <EntranceSubtitle>准备开始学习</EntranceSubtitle>
          <EntranceProgressBar>
            <EntranceProgressFill $progress={entranceProgress} />
          </EntranceProgressBar>
          <EntranceStatus>{entranceStatus}</EntranceStatus>
        </EntranceContent>
      </EntranceOverlay>

      {/* Progress bar at top */}
      <TopProgressBar>
        <ProgressFill $progress={overallProgress} />
      </TopProgressBar>

      {/* Sidebar */}
      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarHeader>
          <SidebarTitle>目录</SidebarTitle>
          <SidebarSubtitle>{course.title}</SidebarSubtitle>
        </SidebarHeader>
        <SidebarNav>
          {course.chapters.map(chapter => {
            const isChapterLocked = !unlockedChapters.has(chapter._id);
            return (
              <ChapterGroup key={chapter._id}>
                <ChapterHeader
                  $expanded={expandedChapters.has(chapter._id)}
                  onClick={() => {
                    if (isChapterLocked) {
                      toast.error('请先完成前一章节的学习');
                      return;
                    }
                    toggleChapter(chapter._id);
                  }}
                  style={{ opacity: isChapterLocked ? 0.6 : 1, cursor: isChapterLocked ? 'not-allowed' : 'pointer' }}
                >
                  <CollapseIcon $collapsed={!expandedChapters.has(chapter._id)}>▼</CollapseIcon>
                  {isChapterLocked && <span style={{ marginRight: '0.5rem' }}>🔒</span>}
                  {chapter.title}
                </ChapterHeader>
                {isCatalogMode ? (
                  <CatalogSectionList $collapsed={!expandedChapters.has(chapter._id)}>
                    {(chapter.sections || []).map((section) => (
                      <ChapterGroup key={section._id}>
                        <CatalogSectionHeader onClick={() => toggleSection(section._id)}>
                          <CollapseIcon $collapsed={!expandedSections.has(section._id)}>▼</CollapseIcon>
                          {section.title}
                        </CatalogSectionHeader>
                        <TopicList $collapsed={!expandedSections.has(section._id)}>
                          {flatTopics
                            .filter(item => item.sectionId === section._id)
                            .map((item, idx) => (
                              <TopicItem
                                key={item.topicId}
                                $active={item.index === currentTopicIndex}
                                $completed={completedTopics.has(item.topicId)}
                                onClick={() => goToTopic(item.index)}
                              >
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {item.topic?.title || `主题 ${idx + 1}`}
                                </span>
                                {completedTopics.has(item.topicId) && (
                                  <span style={{
                                    color: theme.colors.success,
                                    fontWeight: 'bold',
                                    fontSize: '0.75rem',
                                    marginLeft: '0.5rem',
                                    flexShrink: 0,
                                  }}>✓</span>
                                )}
                              </TopicItem>
                            ))}
                        </TopicList>
                      </ChapterGroup>
                    ))}
                  </CatalogSectionList>
                ) : (
                  <TopicList $collapsed={!expandedChapters.has(chapter._id)}>
                    {flatTopics
                      .filter(t => t.chapterId === chapter._id)
                      .map((item, idx) => {
                        const isLocked = isChapterLocked;
                        return (
                          <TopicItem
                            key={item.topicId}
                            $active={item.index === currentTopicIndex}
                            $completed={completedTopics.has(item.topicId)}
                            $locked={isLocked}
                            onClick={() => goToTopic(item.index)}
                          >
                            {isLocked && <span style={{ marginRight: '0.5rem' }}>🔒</span>}
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {item.sectionTitle ? `${item.sectionTitle} - ` : ''}
                              {item.topic?.title || `主题 ${idx + 1}`}
                            </span>
                            {completedTopics.has(item.topicId) && !isLocked && (
                              <span style={{
                                color: theme.colors.success,
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                marginLeft: '0.5rem',
                                flexShrink: 0,
                              }}>✓</span>
                            )}
                          </TopicItem>
                        );
                      })}
                  </TopicList>
                )}
              </ChapterGroup>
            );
          })}
        </SidebarNav>
        <SidebarFooter>
          <UserInfo>
            <UserAvatar>{user?.name?.charAt(0) || '学'}</UserAvatar>
            <UserDetails>
              <UserName>{user?.name || '学习者'}</UserName>
              <UserProgress>进度: {overallProgress}%</UserProgress>
            </UserDetails>
          </UserInfo>
        </SidebarFooter>
      </Sidebar>

      {/* Sidebar Toggle Button */}
      <SidebarToggle onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
        {sidebarCollapsed ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        )}
      </SidebarToggle>

      {/* Main content area */}
      <MainContent>
        {/* Header */}
        <Header>
          <HeaderContent>
            {/* Activity Title */}
            {(() => {
              if (showingSummary && isReviewMode) {
                return (
                  <ActivityTitle>
                    <span>📋</span>
                    <span>{currentTopic?.topic?.title} - 主题回顾</span>
                  </ActivityTitle>
                );
              }
              const activityIcons: Record<string, string> = {
                reading: '📖',
                video: '🎬',
                selfExplanation: '💭',
                survey: '📊',
                review: '🔄',
                exercise: '✏️'
              };
              const icon = activityIcons[currentActivityName || ''] || '📌';
              const title = currentActivityItem?.activity?.title || currentTopic?.topic?.title || '';
              return title ? (
                <ActivityTitle>
                  <span>{icon}</span>
                  <span>{title}</span>
                </ActivityTitle>
              ) : null;
            })()}
          </HeaderContent>
        </Header>

        {/* Scrollable content */}
        <ContentScroll>
          <ContentContainer>
            <ContentFade>
          {/* No activities configured */}
          {sortedActivities.length === 0 ? (
            <>
              <ContentTitle>{currentTopic?.topic?.title || `主题 ${currentTopicIndex + 1}`}</ContentTitle>
              <HtmlContent>
                <p style={{ color: theme.colors.fgMuted }}>
                  该主题暂无学习活动
                </p>
              </HtmlContent>
              <PrimaryButton onClick={markTopicCompleted}>
                {currentTopicIndex < flatTopics.length - 1 ? '继续下一主题' : '完成课程'}
                <span>→</span>
              </PrimaryButton>
            </>
          ) : showingSummary && isReviewMode ? (
            /* Topic Summary Card for completed topics */
            <>
              <div style={{
                background: theme.colors.card,
                borderRadius: '12px',
                border: `1px solid ${theme.colors.border}`,
                overflow: 'hidden'
              }}>
                {/* Card Header */}
                <div style={{
                  padding: '1.5rem',
                  background: `linear-gradient(135deg, ${theme.colors.accentLight}, ${theme.colors.bgDark})`,
                  borderBottom: `1px solid ${theme.colors.border}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <ContentTitle style={{ margin: 0, fontSize: '1.25rem' }}>
                      {currentTopic?.topic?.title || `主题 ${currentTopicIndex + 1}`}
                    </ContentTitle>
                    <span style={{
                      background: theme.colors.success,
                      color: 'white',
                      padding: '0.2rem 0.6rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      已完成
                    </span>
                  </div>
                  <p style={{ color: theme.colors.fgMuted, fontSize: '0.85rem', margin: 0 }}>
                    你已完成本主题的所有学习活动。可以回顾和复习以下内容。
                  </p>
                </div>

                {/* Activity List */}
                <div style={{ padding: '0.5rem 1.5rem' }}>
                  {sortedActivities.map((item, idx) => {
                    const activityId = item.activity._id || item.activity.name;
                    const activityKey = `${currentTopic?.topicId}:${activityId}`;
                    const response = reviewActivityResponses.get(activityKey);
                    const activityIcons: Record<string, string> = {
                      reading: '📖', video: '🎬', selfExplanation: '💭',
                      survey: '📊', review: '🔄', exercise: '✏️'
                    };
                    const icon = activityIcons[item.activity.name] || '📌';

                    return (
                      <div key={idx} style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.875rem 1rem',
                        borderBottom: idx < sortedActivities.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                        gap: '0.75rem'
                      }}>
                        <span style={{ fontSize: '1.25rem' }}>{icon}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: theme.colors.fg }}>
                            {item.activity.title || `活动 ${idx + 1}`}
                          </div>
                          {item.activity.name === 'selfExplanation' && response?.selfExplanationData?.explanation && (
                            <div style={{
                              color: theme.colors.fgMuted,
                              fontSize: '0.8rem',
                              marginTop: '0.25rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '400px'
                            }}>
                              "{response.selfExplanationData.explanation.substring(0, 60)}..."
                            </div>
                          )}
                          {item.activity.name === 'exercise' && response?.exerciseData && (
                            <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                              {response.exerciseData.masteryStatus === 'mastered' ? (
                                <span style={{ color: theme.colors.success }}>✓ 已掌握</span>
                              ) : (
                                <span style={{ color: '#f59e0b' }}>需要复习</span>
                              )}
                            </div>
                          )}
                        </div>
                        <PrimaryButton
                          style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                          onClick={() => goToActivity(idx)}
                        >
                          {item.activity.name === 'reading' ? '重新阅读' :
                           item.activity.name === 'selfExplanation' ? '查看/编辑' :
                           item.activity.name === 'exercise' ? '再次练习' :
                           '查看'}
                        </PrimaryButton>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Continue to next topic button */}
              {currentTopicIndex < flatTopics.length - 1 && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <PrimaryButton onClick={goToNextTopic}>
                    继续学习下一主题 →
                  </PrimaryButton>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Reading Activity */}
              {currentActivityName === 'reading' && (
                <>
                  {isReviewMode && !showingSummary && (
                    <NavButton onClick={goBackToSummary} style={{ marginBottom: '1rem' }}>
                      ← 返回主题总结
                    </NavButton>
                  )}
                  {(() => {
                    const readingLink = currentActivityItem?.activity?.link;
                    const isExternalLink = readingLink && (readingLink.startsWith('http://') || readingLink.startsWith('https://'));

                    if (isExternalLink) {
                      // External HTTP link - display in iframe
                      return (
                        <div style={{
                          width: '100%',
                          marginTop: '1rem',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: `1px solid ${theme.colors.border}`,
                          background: '#fff'
                        }}>
                          <iframe
                            key={timingVisitKey}
                            onLoad={() => setReadyTimingKey(timingVisitKey)}
                            src={readingLink}
                            style={{
                              width: '100%',
                              height: '70vh',
                              border: 'none',
                              minHeight: '500px'
                            }}
                            title="阅读内容"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                          />
                          <div style={{
                            padding: '0.75rem 1rem',
                            background: theme.colors.card,
                            borderTop: `1px solid ${theme.colors.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}>
                            <span style={{ color: theme.colors.fgMuted, fontSize: '0.85rem' }}>
                              📎 来源:
                            </span>
                            <a
                              href={readingLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: theme.colors.accentSecondary, fontSize: '0.85rem', wordBreak: 'break-all' }}
                            >
                              在新窗口打开
                            </a>
                          </div>
                        </div>
                      );
                    } else {
                      // Topic content or no external link
                      return (
                        <>
                          {latestHtml ? (
                            /<(script|style|button)[\s>]/i.test(latestHtml) ? (
                              <SandboxedHTMLContent key={timingVisitKey} html={latestHtml} timingKey={timingVisitKey} onReady={() => setReadyTimingKey(timingVisitKey)} />
                            ) : (
                              <HtmlContent dangerouslySetInnerHTML={{ __html: renderLatexEquations(latestHtml) }} />
                            )
                          ) : (
                            <HtmlContent>
                              <p style={{ color: theme.colors.fgMuted }}>
                                {currentTopic?.topic?.description || '暂无学习内容'}
                              </p>
                            </HtmlContent>
                          )}
                        </>
                      );
                    }
                  })()}

                  {/* Reading completion checkbox */}
                  <div style={{
                    marginTop: '2rem',
                    padding: '1rem 1.5rem',
                    background: readingCompleted ? 'rgba(45, 106, 79, 0.08)' : theme.colors.card,
                    borderRadius: '12px',
                    border: `2px solid ${readingCompleted ? theme.colors.success : theme.colors.border}`,
                    transition: theme.transitions.base
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}>
                      <input
                        type="checkbox"
                        checked={readingCompleted}
                        onChange={(e) => handleReadingCheckboxChange(e.target.checked)}
                        style={{
                          width: '22px',
                          height: '22px',
                          cursor: 'pointer',
                          accentColor: theme.colors.success
                        }}
                      />
                      <span style={{
                        fontSize: '1rem',
                        fontWeight: 500,
                        color: readingCompleted ? theme.colors.success : theme.colors.fg
                      }}>
                        ✓ 我已完成阅读学习材料
                      </span>
                    </label>
                  </div>

                  <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                    <PrimaryButton
                      onClick={isReviewMode ? goBackToSummary : handleReadingComplete}
                      disabled={!isReviewMode && !readingCompleted}
                    >
                      {isReviewMode ? '返回主题总结' :
                       readingCompleted ? '完成阅读，继续下一步' : '请先确认完成阅读'}
                      <span>→</span>
                    </PrimaryButton>
                  </div>
                </>
              )}

              {/* Video Activity */}
              {currentActivityName === 'video' && (
                <>
                  <VideoPlayer
                    key={timingVisitKey}
                    onReady={() => setReadyTimingKey(timingVisitKey)}
                    url={currentActivityItem?.activity?.link || currentActivityItem?.activity?.value || ''}
                    title={currentActivityItem?.activity?.title}
                  />
                  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                    <PrimaryButton onClick={handleVideoComplete}>
                      完成视频观看，继续下一步
                      <span>→</span>
                    </PrimaryButton>
                    {currentActivityIndex > 0 && (
                      <NavButton style={{ marginTop: '0.5rem' }} onClick={goToPrevActivity}>
                        ← 返回上一步
                      </NavButton>
                    )}
                  </div>
                </>
              )}

              {/* Self-Explanation Activity */}
              {currentActivityName === 'selfExplanation' && (
                <SelfExplanationSection style={{ marginTop: 0 }}>
                  {isReviewMode && !showingSummary && (
                    <NavButton onClick={goBackToSummary} style={{ marginBottom: '1rem' }}>
                      ← 返回主题总结
                    </NavButton>
                  )}
                  <SectionLabel $color={theme.colors.accentSecondary}>
                    ✍️ 自我解释
                  </SectionLabel>
                  <SectionHint>
                    用你自己的话解释一下你对「{currentTopic?.topic?.title || '这个概念'}」的理解。这有助于巩固学习效果。
                  </SectionHint>
                  <ExplanationTextarea
                    placeholder="在这里写下你的理解..."
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    autoFocus
                  />
                  <UnderstandingLevel>
                    <LevelLabel>你的理解程度：</LevelLabel>
                    <LevelButtons>
                      <LevelButton
                        $active={understandingLevel === 'low'}
                        onClick={() => setUnderstandingLevel('low')}
                      >
                        还需学习
                      </LevelButton>
                      <LevelButton
                        $active={understandingLevel === 'medium'}
                        onClick={() => setUnderstandingLevel('medium')}
                      >
                        基本理解
                      </LevelButton>
                      <LevelButton
                        $active={understandingLevel === 'high'}
                        onClick={() => setUnderstandingLevel('high')}
                      >
                        完全掌握
                      </LevelButton>
                    </LevelButtons>
                  </UnderstandingLevel>
                  <SubmitButton
                    $disabled={!explanation.trim() || !understandingLevel}
                    onClick={isReviewMode ? handleSelfExplanationReviewSave : handleSelfExplanationSubmit}
                  >
                    {isReviewMode ? '保存修改' : '提交并继续'}
                    <span>→</span>
                  </SubmitButton>
                  {!isReviewMode && currentActivityIndex > 0 && (
                    <NavButton
                      style={{ marginTop: '1rem', justifyContent: 'center' }}
                      onClick={goToPrevActivity}
                    >
                      ← 返回上一步
                    </NavButton>
                  )}
                </SelfExplanationSection>
              )}

              {/* Survey Activity */}
              {currentActivityName === 'survey' && (
                <SurveyContainer style={{ marginTop: 0 }}>
                  <SectionLabel $color={theme.colors.accentText}>
                    📊 问卷
                  </SectionLabel>
                  <SectionHint>
                    {currentActivityItem?.activity?.title || '请完成以下问卷'}
                  </SectionHint>
                  {currentSurvey ? (
                    <SurveyContent dangerouslySetInnerHTML={{ __html: currentSurvey.htmlContent || '' }} />
                  ) : (
                    <div style={{ padding: '2rem', textAlign: 'center', color: theme.colors.fgMuted }}>
                      加载问卷内容...
                    </div>
                  )}
                  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                    <PrimaryButton onClick={handleSurveyComplete}>
                      完成问卷，继续下一步
                      <span>→</span>
                    </PrimaryButton>
                    {currentActivityIndex > 0 && (
                      <NavButton style={{ marginTop: '0.5rem' }} onClick={goToPrevActivity}>
                        ← 返回上一步
                      </NavButton>
                    )}
                  </div>
                </SurveyContainer>
              )}

              {/* Exercise Activity - handles both 'exercise' and 'quiz' activity types */}
              {(currentActivityName === 'exercise' || currentActivityName === 'quiz') && (
                <>
                  {isReviewMode && !showingSummary && (
                    <NavButton onClick={goBackToSummary} style={{ marginBottom: '1rem' }}>
                      ← 返回主题总结
                    </NavButton>
                  )}
                  {hasExercise ? (
                    <>
                      {/* Check if content has scripts/styles that need sandboxed rendering */}
                      {/<(script|style|button)[\s>]/i.test(exerciseContent || '') ? (
                        <SandboxedHTMLContent key={timingVisitKey} html={exerciseContent || ''} timingKey={timingVisitKey} onReady={() => setReadyTimingKey(timingVisitKey)} />
                      ) : (
                        <ExerciseContainer>
                          <ExerciseContent dangerouslySetInnerHTML={{
                            __html: renderLatexEquations(exerciseContent || '')
                          }} />
                        </ExerciseContainer>
                      )}

                      {/* Mastery Progress Indicator */}
                      <MasteryProgressIndicator>
                        <span>掌握进度：</span>
                        <ProgressCount $color={
                          masteryStatus.masteryStatus === 'mastered' ? theme.colors.success :
                          masteryStatus.masteryStatus === 'needs_review' ? '#f59e0b' :
                          theme.colors.fg
                        }>
                          连续正确 {masteryStatus.consecutiveCorrect}/3
                        </ProgressCount>
                        {masteryStatus.masteryStatus === 'mastered' && (
                          <span style={{ marginLeft: '0.5rem', color: theme.colors.success }}>✓ 已掌握</span>
                        )}
                        {masteryStatus.masteryStatus === 'needs_review' && (
                          <span style={{ marginLeft: '0.5rem', color: '#f59e0b' }}>需要复习</span>
                        )}
                      </MasteryProgressIndicator>

                      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                        <PrimaryButton
                          onClick={isReviewMode ? goBackToSummary : handleExerciseComplete}
                          disabled={!isReviewMode && masteryStatus.masteryStatus !== 'mastered'}
                          $disabled={!isReviewMode && masteryStatus.masteryStatus !== 'mastered'}
                        >
                          {isReviewMode ? '返回主题总结' :
                           masteryStatus.masteryStatus === 'mastered'
                            ? '完成练习，继续下一步'
                            : masteryStatus.masteryStatus === 'needs_review'
                              ? '建议重新学习后继续'
                              : '继续练习以掌握该主题'}
                          <span>→</span>
                        </PrimaryButton>
                        {!isReviewMode && currentActivityIndex > 0 && (
                          <NavButton
                            style={{ marginTop: '0.5rem' }}
                            onClick={goToPrevActivity}
                          >
                            ← 返回上一步
                          </NavButton>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        padding: '2rem',
                        background: theme.colors.bgDark,
                        borderRadius: '12px',
                        textAlign: 'center',
                        marginBottom: '1rem'
                      }}>
                        <p style={{ color: theme.colors.fgMuted, margin: 0 }}>
                          📋 该主题暂无练习题
                        </p>
                        <p style={{ color: theme.colors.fgMuted, fontSize: '0.875rem', marginTop: '0.5rem' }}>
                          练习题正在准备中，请稍后再来
                        </p>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <PrimaryButton onClick={goToNextActivity}>
                          {currentActivityIndex < sortedActivities.length - 1 ? '继续下一步' :
                           currentTopicIndex < flatTopics.length - 1 ? '完成并学习下一主题' : '完成课程'}
                          <span>→</span>
                        </PrimaryButton>
                        {currentActivityIndex > 0 && (
                          <NavButton
                            style={{ marginTop: '0.5rem' }}
                            onClick={goToPrevActivity}
                          >
                            ← 返回上一步
                          </NavButton>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Review Activity (placeholder) */}
              {currentActivityName === 'review' && (
                <>
                  <HtmlContent>
                    <p style={{ color: theme.colors.fgMuted }}>
                      复习活动正在准备中...
                    </p>
                  </HtmlContent>
                  <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                    <PrimaryButton onClick={goToNextActivity}>
                      继续
                      <span>→</span>
                    </PrimaryButton>
                    {currentActivityIndex > 0 && (
                      <NavButton style={{ marginTop: '0.5rem' }} onClick={goToPrevActivity}>
                        ← 返回上一步
                      </NavButton>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </ContentFade>
      </ContentContainer>

      {/* Bottom navigation - show topic navigation */}
      <BottomNav>
        <BottomNavContent>
          <NavButton
            $disabled={currentTopicIndex === 0}
            onClick={goToPrevTopic}
          >
            <span>←</span>
            <span>上一主题</span>
          </NavButton>

          {/* Activity progress indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {(() => {
              // Find the first incomplete activity index
              let firstIncompleteIdx = -1;
              for (let i = 0; i < sortedActivities.length; i++) {
                const item = sortedActivities[i];
                const activityId = item.activity._id || item.activity.name;
                const activityKey = `${currentTopic?.topicId}:${activityId}`;
                if (!completedActivityIds.has(activityKey)) {
                  firstIncompleteIdx = i;
                  break;
                }
              }

              return sortedActivities.map((item, idx) => {
                const activityId = item.activity._id || item.activity.name;
                const activityKey = `${currentTopic?.topicId}:${activityId}`;
                const isCompleted = completedActivityIds.has(activityKey);
                const isCurrent = idx === currentActivityIndex;
                const isNext = idx === firstIncompleteIdx && !isCurrent;
                // In review mode, all dots are clickable; otherwise only completed/current/first-incomplete
                const isClickable = isReviewMode ? true : (isCompleted || isCurrent || idx === firstIncompleteIdx);

                return (
                  <button
                    key={idx}
                    onClick={() => isClickable && goToActivity(idx)}
                    disabled={!isClickable}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: 'none',
                      // Review mode: green or muted; Normal: completed=green, current=accent, next=orange, else=gray
                      background: isReviewMode
                        ? (isCompleted ? theme.colors.success : theme.colors.fgMuted)
                        : (isCompleted ? theme.colors.success :
                           isCurrent ? theme.colors.accent :
                           isNext ? '#f59e0b' : theme.colors.border),
                      transition: 'all 0.2s ease',
                      cursor: isClickable ? 'pointer' : 'not-allowed',
                      padding: 0,
                      opacity: isClickable ? 1 : 0.6,
                      // Scale up current activity
                      transform: isCurrent ? 'scale(1.3)' : 'scale(1)',
                    }}
                    title={isClickable ? `跳转到活动 ${idx + 1}` : '请先完成前面的活动'}
                  />
                );
              });
            })()}
          </div>

          <NavButton
            $disabled={currentTopicIndex === flatTopics.length - 1}
            onClick={goToNextTopic}
          >
            <span>下一主题</span>
            <span>→</span>
          </NavButton>
        </BottomNavContent>
      </BottomNav>
        </ContentScroll>
      </MainContent>

      {/* Mastery Celebration Modal */}
      <ModalOverlay $visible={showMasteryCelebration}>
        <MasteryModal>
          <MasteryIcon $type="success">🎉</MasteryIcon>
          <MasteryTitle>恭喜你！</MasteryTitle>
          <MasteryMessage>
            你已经连续答对3道题，成功掌握了这个主题！
            <br />
            继续保持，向下一个主题进发吧！
          </MasteryMessage>
          <MasteryProgress>
            {[1, 2, 3].map(i => (
              <ProgressDot key={i} $filled={true} />
            ))}
          </MasteryProgress>
          <ModalButtons>
            <ModalButton
              $primary
              onClick={() => {
                setShowMasteryCelebration(false);
                goToNextActivity();
              }}
            >
              继续下一步
            </ModalButton>
          </ModalButtons>
        </MasteryModal>
      </ModalOverlay>

      {/* Relearn Prompt Modal */}
      <ModalOverlay $visible={showRelearnPrompt}>
        <MasteryModal>
          <MasteryIcon $type="warning">📚</MasteryIcon>
          <MasteryTitle>建议重新学习</MasteryTitle>
          <MasteryMessage>
            看起来这个主题对你有些挑战。
            <br />
            建议重新阅读学习材料，加深理解后再来尝试！
          </MasteryMessage>
          <ModalButtons>
            <ModalButton
              onClick={() => {
                setShowRelearnPrompt(false);
                // Go back to reading activity
                const readingIndex = sortedActivities.findIndex(
                  a => a.activity?.name === 'reading'
                );
                if (readingIndex >= 0) {
                  goToActivity(readingIndex);
                }
              }}
            >
              重新学习
            </ModalButton>
            <ModalButton
              $primary
              onClick={() => {
                setShowRelearnPrompt(false);
                // Reset consecutive incorrect and continue with new question
                setMasteryStatus(prev => ({
                  ...prev,
                  consecutiveIncorrect: 0,
                  masteryStatus: 'in_progress'
                }));
              }}
            >
              继续尝试
            </ModalButton>
          </ModalButtons>
        </MasteryModal>
      </ModalOverlay>
    </PageContainer>
  );
};

export const StudentCourseLearnPage: React.FC = () => (
  <CourseLearnPage navigationMode="course" />
);

export const PrivilegedTopicLearnPage: React.FC = () => (
  <CourseLearnPage navigationMode="catalog" />
);

export default StudentCourseLearnPage;
